import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Send,
  Check,
  X as XIcon,
  Upload,
  Download,
  FileText,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Mail,
  AlertTriangle,
  ShieldAlert,
  Undo2,
  HelpCircle,
  MailQuestion,
  Gavel,
  Trash2,
  Pencil,
  History as HistoryIcon,
  Lock,
  LockOpen,
  Eye,
  Trophy,
  CircleSlash,
  Plus,
  Copy,
} from 'lucide-react';
import { rfqAPI, quotationAPI, configAPI, userAPI, offerAPI } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { t } from '@/i18n/translations';
import EssentialDocumentsSection from '@/components/EssentialDocumentsSection';
import PnLAuditPanel from '@/components/PnLAuditPanel';
import { formatDate, formatEuro, formatNumber } from '@/lib/utils';
import { formatApiError } from '@/lib/apiError';
import { quotationUpdatesFromSuggestions } from '@/lib/quotationSuggestions';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredLabel } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  CardFoldProvider,
  CardFoldToolbar,
  CollapsibleCard,
} from '@/components/ui/collapsible-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge, statusBadgeVariant } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  isOverdue,
  piecesPerDayWithoutScrap,
  applyAutoPiecesPerBox,
  applyAutoBoxesPerPallet,
  diffKeys,
  extractRiskFromJustification,
  extractReworkVersion,
  extractFormVersion,
  finishingOpLabel,
  g2Text,
  g2Day,
  g2DateInputToIso,
  g2BlurGoesToSubmit,
  buildG2MetaPatch,
  getSubmitReasons,
  canUnlockEdit,
  canLockEdit,
  canSubmitToCommercialMargins,
  buildFinishingOpsFromConfig,
  canonicalFinishingOp,
  bucketVmLabel,
  bucketVaInternalLabel,
  bucketExtraWorksLabel,
  bucketPackagingLabel,
  bucketTransportLabel,
  bucketComponentsLabel,
  bucketSubcontractLabel,
  DIFF_RING,
  SUGGESTED_RING,
  FieldLabel,
  NumField,
  IntField,
  PercentField,
  SupplierSelect,
  ReadOnlyNumField,
  formatSnapshotValue,
  VersionSnapshotView,
  COLLECTION_TITLE_KEYS,
  SNAPSHOT_LABEL_KEYS,
  newRowUid,
  normalisePositions,
  ensureStableRowUids,
} from './rfqDetail/helpers';

// Nº mínimo de caracteres exigido no motivo de override manual do Base MB
// (deve coincidir com BASE_MB_COMMENT_MIN_LEN no backend).
const BASE_MB_COMMENT_MIN_LEN = 50;

// Serializa a cotação para detecção de alterações, EXCLUINDO campos geridos
// pelo servidor: ``revision`` (incrementada a cada PUT), ``pnl_audit_snapshot``
// e ``calc_snapshot`` (recalculados pelo backend e devolvidos na resposta).
// Se entrassem no snapshot de comparação, cada gravação/refresh mudaria o
// snapshot e dispararia um novo auto-save em ciclo infinito. O backend ignora
// estes campos no payload do PUT, pelo que excluí-los é seguro.
const serializeQuotationForCompare = (q) => {
  if (!q) return '';
  const {
    revision, pnl_audit_snapshot: _pnl, calc_snapshot: _calc, ...rest
  } = q;
  return JSON.stringify(rest);
};

// Coleções que ainda usam _key temporário (tooling / estudos — fora do âmbito desta correcção).
const _LEGACY_KEY_COLLECTIONS = ['tooling_lines', 'study_lines'];
let _rowKeySeq = 0;
const _newRowKey = () => `row_${Date.now()}_${(_rowKeySeq += 1)}`;

function withLegacyRowKeys(serverData, prev) {
  if (!serverData) return serverData;
  const next = { ...serverData };
  for (const key of _LEGACY_KEY_COLLECTIONS) {
    if (!Array.isArray(serverData[key])) continue;
    const prevList = prev && Array.isArray(prev[key]) ? prev[key] : [];
    next[key] = serverData[key].map((row, i) => ({
      ...row,
      _key: (prevList[i] && prevList[i]._key) || row._key || _newRowKey(),
    }));
  }
  return next;
}

export default function RFQDetailPage() {
  const { id } = useParams();
  const { lang, user } = useAuth();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const rfqRef = useRef(null);
  const [quotation, setQuotation] = useState(null);
  const [alloys, setAlloys] = useState([]);
  // Versões de preço da liga atualmente selecionada (para o seletor de versão).
  const [alloyVersions, setAlloyVersions] = useState([]);
  const [boxTypes, setBoxTypes] = useState([]);
  // Catálogo de fornecedores (admin) usado para popular o select
  // "Fornecedor" do card de Subcontratação. Filtramos por categoria
  // directamente no JSX (mais simples que carregar 4× separadamente).
  const [suppliers, setSuppliers] = useState([]);
  const [machines, setMachines] = useState([]);
  // Finishing rates configurados pelo admin — fonte canónica de tarifas €/h.
  const [finishingRates, setFinishingRates] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignEngineerId, setAssignEngineerId] = useState('');
  const [history, setHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [calcResult, setCalcResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  // Auto-save com debounce: 'idle' | 'pending' | 'saving' | 'saved' | 'error'.
  // O painel "Validação de preço" reflecte o snapshot persistido, por isso
  // este auto-save é a única via para o utilizador ver a validação a
  // recalcular quando muda inputs da cotação.
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  // Hash JSON do último estado da cotação efectivamente persistido. Serve
  // para evitar disparar auto-save em mudanças de identidade (re-set após
  // resposta do servidor) ou em renders sem mudança real.
  const lastPersistedQuotationRef = useRef(null);
  // Espelho síncrono do estado ``quotation`` mais recente. Usado dentro dos
  // handlers assíncronos de gravação para detectar se o utilizador editou
  // durante um pedido em curso (ver ``runAutoSave``). Escrever num ref durante
  // o render é seguro para este padrão de "valor mais recente".
  const quotationRef = useRef(null);
  // Timer do debounce e flag para suprimir o save inicial (logo após
  // loadAll). Evita disparar uma chamada redundante por causa do setQuotation
  // do load.
  const autoSaveTimerRef = useRef(null);
  const skipNextAutoSaveRef = useRef(true);
  // Fila serializada de gravação — nunca dois PUTs em paralelo; latest wins.
  const saveInFlightRef = useRef(false);
  const pendingSaveRef = useRef(null);
  // Timer de retry após erro de gravação — com atraso, para nunca entrar em
  // loop apertado de PUTs quando o servidor recusa persistentemente.
  const saveRetryTimerRef = useRef(null);
  const revisionRef = useRef(0);
  const saveWaitersRef = useRef([]);
  // Último RFQ efectivamente persistido (G2). O onBlur compara com isto, não
  // com o estado local — senão o PUT é saltado depois do onChange.
  const rfqPersistedRef = useRef(null);
  const [expandedSnapshot, setExpandedSnapshot] = useState(null);

  // IMPORTANT: manter o ref em sync *durante o render*. Se usarmos um
  // useEffect, existe uma janela em que o estado já mudou mas o ref ainda não —
  // e uma resposta rápida do auto-save pode sobrescrever as edições recentes.
  quotationRef.current = quotation;

  // Workflow dialog state — a single modal reused for every action that requires a comment.
  const [workflowDialog, setWorkflowDialog] = useState(null); // { action, title, desc, needsRisk, minChars }
  const [dialogComment, setDialogComment] = useState('');
  const [dialogRisk, setDialogRisk] = useState('medium');
  const [dialogBusy, setDialogBusy] = useState(false);
  // Extra state used only by the request_client_info dialog flavour.
  const [dialogSendEmail, setDialogSendEmail] = useState(true);
  const [dialogEmailTo, setDialogEmailTo] = useState('');
  // Caixa de selecção dos 4 emails departamentais do cliente.
  // Quando vazio ou 'custom', cai-se no input livre dialogEmailTo.
  const [dialogRecipientRole, setDialogRecipientRole] = useState('');

  // Form-level versioning state
  const [versions, setVersions] = useState([]);
  const [versionDetail, setVersionDetail] = useState(null); // opened in comparison dialog
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockComment, setLockComment] = useState('');
  const [lockBusy, setLockBusy] = useState(false);

  // Commercial outcome dialog (won / lost)
  const [outcomeDialog, setOutcomeDialog] = useState(null); // 'won' | 'lost' | null
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [outcomeLossReason, setOutcomeLossReason] = useState('price');
  const [outcomeBusy, setOutcomeBusy] = useState(false);
  const [crossNoteText, setCrossNoteText] = useState('');
  const [crossNoteBusy, setCrossNoteBusy] = useState(false);

  // Offer PDF state
  const [offers, setOffers] = useState([]);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  // Planning weeks defaults — espelham o cfg global (12/2/8/6) e ficam
  // pré-preenchidos no diálogo "Gerar Offer" para que, na 2ª (e seguintes)
  // vez que o comercial recebe a proposta, possa ajustar prazos directos
  // sem ter de mexer na configuração global.
  const [offerForm, setOfferForm] = useState({
    observations: '',
    validity_days: '',
    specific_conditions: '',
    // Vazios de propósito: em branco herdam os payment terms do cliente e,
    // na falta destes, os defaults globais.
    payment_terms_parts: '',
    payment_terms_tooling: '',
    planning_first_article_weeks: '12',
    planning_first_series_weeks: '2',
    planning_supplying_weeks: '8',
    planning_supplying_forecast_weeks: '6',
    language: 'en',
  });
  const [offerBusy, setOfferBusy] = useState(false);

  // Pré-preenchimento a partir do desenho 2D — paths que foram aplicados
  // automaticamente OU manualmente via QuotationSuggestionsPanel (usado para
  // mostrar o badge "Sugerido pelo desenho 2D" nos campos do formulário).
  // Estado de sessão (não persistido); uma vez que o utilizador toca no
  // campo, o badge desaparece (gerido por `clearSuggestedField`).
  const [suggestedFields, setSuggestedFields] = useState(() => new Set());

  const crossProfileNotes = useMemo(
    () => (notes || []).filter((n) => n.section === 'cross_profile'),
    [notes],
  );

  const loadAll = useCallback(async () => {
    try {
      const [rfqRes, qRes, alloysRes, boxTypesRes, suppliersRes, machinesRes, histRes, docsRes, notesRes, usersRes, versionsRes, offersRes, finishingRatesRes] = await Promise.all([
        rfqAPI.get(id),
        quotationAPI.get(id).catch(() => ({ data: null })),
        configAPI.getAlloys(),
        configAPI.getBoxTypes().catch(() => ({ data: [] })),
        configAPI.getSuppliers().catch(() => ({ data: [] })),
        configAPI.getMachines(),
        rfqAPI.getHistory(id),
        rfqAPI.listDocuments(id),
        rfqAPI.listNotes(id).catch(() => ({ data: [] })),
        userAPI.listAssignable().catch((err) => {
          toast.error(formatApiError(err, t('error_load_assignable', lang)));
          return { data: [] };
        }),
        rfqAPI.listVersions(id).catch(() => ({ data: [] })),
        offerAPI.list(id).catch(() => ({ data: [] })),
        configAPI.getFinishingRates(false, 'all').catch(() => ({ data: [] })),
      ]);
      setRfq(rfqRes.data);
      rfqRef.current = rfqRes.data;
      rfqPersistedRef.current = rfqRes.data;
      const q = qRes.data || {};
      const allRates = finishingRatesRes.data || [];
      const finishingOps = buildFinishingOpsFromConfig(allRates, 'finishing');
      const extraWorksOps = buildFinishingOpsFromConfig(allRates, 'extra_works');
      setFinishingRates(allRates);
      // Lookup global rate→€/h para reaplicar tarifa actual da config a ops
      // já gravadas (acabamento + trabalhos extra).
      const rateMap = new Map(allRates.map((r) => [r.operation_name, r.default_rate_eur_hr]));
      const reapplyRate = (op) => {
        const canonical = canonicalFinishingOp(op.name);
        const rate = rateMap.get(canonical);
        return rate != null ? { ...op, rate_eur_hr: rate } : op;
      };
      if (!q.finishing_operations || q.finishing_operations.length === 0) {
        q.finishing_operations = finishingOps;
      } else {
        q.finishing_operations = q.finishing_operations.map(reapplyRate);
      }
      if (!q.extra_works_operations || q.extra_works_operations.length === 0) {
        q.extra_works_operations = extraWorksOps;
      } else {
        q.extra_works_operations = q.extra_works_operations.map(reapplyRate);
      }
      // calc_v2: ensure child collections exist (as empty arrays) so map/length
      // work without defensive checks across the form. Tooling lines get the
      // canonical 4-line scaffold with default margins (15/15/50/20 %).
      if (!Array.isArray(q.components)) q.components = [];
      if (!Array.isArray(q.subcontract_candidates)) q.subcontract_candidates = [];
      if (!Array.isArray(q.tooling_lines) || q.tooling_lines.length === 0) {
        q.tooling_lines = [
          { label: 'mould_foundry', cost_eur: 0, margin_percent: 0.15, guarantee_shots: null, position: 0 },
          { label: 'mould_cutting', cost_eur: 0, margin_percent: 0.15, guarantee_shots: null, position: 1 },
          { label: 'gabarit_machining', cost_eur: 0, margin_percent: 0.50, guarantee_shots: null, position: 2 },
          { label: 'gabarit_control', cost_eur: 0, margin_percent: 0.20, guarantee_shots: null, position: 3 },
        ];
      }
      if (!Array.isArray(q.study_lines)) q.study_lines = [];
      if (q.inventory_shrink == null) q.inventory_shrink = 0.05;
      q.max_pallet_weight_kg = 1500;
      if (!q.transport_incoterm) q.transport_incoterm = 'exwork';
      // row_uid estável (components/subcontratação) + _key legacy (tooling/estudos).
      const qKeyed = withLegacyRowKeys(ensureStableRowUids(q), null);
      revisionRef.current = qKeyed.revision ?? 0;
      // Auto-save: o setQuotation seguinte (load) NÃO deve disparar save.
      // Marcamos a baseline com o estado acabado de carregar.
      lastPersistedQuotationRef.current = serializeQuotationForCompare(qKeyed);
      skipNextAutoSaveRef.current = true;
      setQuotation(qKeyed);
      setAlloys(alloysRes.data);
      setBoxTypes(boxTypesRes.data || []);
      setSuppliers(suppliersRes.data || []);
      setMachines(machinesRes.data);
      setUsers(usersRes.data || []);
      setHistory(histRes.data);
      setDocuments(docsRes.data);
      setNotes(notesRes.data || []);
      setVersions(versionsRes.data || []);
      setOffers(offersRes.data || []);
      if (q.calc_snapshot) setCalcResult(q.calc_snapshot);
    } catch {
      toast.error(t('error_loading_rfq', lang));
    } finally {
      setLoading(false);
    }
  }, [id, lang]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Refresh periódico do cálculo/validação SEM tocar no formulário.
  // Isto evita que o utilizador veja \"refresh subtil\" enquanto edita linhas
  // (Componentes/Subcontratação), mas mantém o painel de resultado actualizado.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await quotationAPI.getCalc(id);
        if (cancelled) return;
        if (res?.data?.calc_snapshot) setCalcResult(res.data.calc_snapshot);
        // O card "Validação de preço" lê quotation.pnl_audit_snapshot: mantém-no
        // fresco também via polling. Só actualiza se mudou (comparação JSON),
        // para não criar re-renders/resets de timers a cada 15s. O campo está
        // excluído de serializeQuotationForCompare, pelo que não dispara auto-save.
        if (res?.data?.pnl_audit_snapshot) {
          const freshJson = JSON.stringify(res.data.pnl_audit_snapshot);
          setQuotation((prev) => {
            if (!prev) return prev;
            if (JSON.stringify(prev.pnl_audit_snapshot ?? null) === freshJson) return prev;
            return { ...prev, pnl_audit_snapshot: res.data.pnl_audit_snapshot };
          });
        }
      } catch {
        // best-effort: não bloquear UI por falhas de refresh
      }
    };
    // Primeira atualização após carregar.
    tick();
    const interval = setInterval(tick, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id]);

  const isEngineerResponsible =
    rfq && user && (user.is_admin || rfq.engineering_responsible === user.id);

  // Last sealed version is the baseline for highlighting current edits.
  const latestVersion = versions.length ? versions[versions.length - 1] : null;
  const [baselineSnapshot, setBaselineSnapshot] = useState(null);

  // Fetch the full snapshot of the latest sealed version whenever it changes,
  // so we can diff current edits against it for the amber highlight.
  const latestVersionId = latestVersion?.id;
  useEffect(() => {
    if (!latestVersionId) {
      setBaselineSnapshot(null);
      return undefined;
    }
    let cancelled = false;
    rfqAPI.getVersion(id, latestVersionId)
      .then((res) => { if (!cancelled) setBaselineSnapshot(res.data.rfq_snapshot || null); })
      .catch(() => { if (!cancelled) setBaselineSnapshot(null); });
    return () => { cancelled = true; };
  }, [id, latestVersionId]);

  const changedFields =
    rfq?.edit_mode && baselineSnapshot
      ? diffKeys(baselineSnapshot, rfq, quotation)
      : new Set();

  // Inline form fields intentionally do NOT draw the amber "changed" ring —
  // the diff visualisation lives in the version detail popup
  // (VersionSnapshotView + compare-table), which is the canonical place to
  // review modifications. `changedFields` is still used for the edit-mode
  // banner count ("N campos alterados").
  const highlight = () => false;

  const pendingClientNotes = notes.filter(
    (n) => n.section === 'awaiting_client' && !n.resolved
  );
  const awaitingClient = pendingClientNotes.length > 0;

  // O engenheiro deixou de ser pré-requisito da submissão: passa a ser
  // atribuído pelo Diretor Técnico de Engenharia após esta submissão.
  const submitReasons = getSubmitReasons({
    rfq,
    documents,
    awaitingClient,
    t,
    lang,
  });

  // MACH-02 — área projectada (cm²) deriva de length × width × projection_coef
  // (mm² → cm² ÷ 100). O coeficiente de projecção (default 0.7) reflecte a
  // fracção da bounding box L×W que é de facto ocupada pela peça —
  // peças que preenchem quase todo o rectângulo aproximam-se de 0.9–1.0.
  // A tonelagem (T) deriva de
  // (área × cavidades × pressão × (1+coef_gito) × (1+coef_gavetas) × (1+coef_segurança)) / 1000.
  // Todos os coeficientes são introduzidos como 0–1 (rev Ago 2026).
  // Idêntica à fórmula aplicada no backend (blocks/machine.py).
  const _recomputeProjectedArea = (q) => {
    const L_mm = Number(q.piece_length_mm) || 0;
    const W_mm = Number(q.piece_width_mm) || 0;
    const coef = Number.isFinite(Number(q.projection_coef))
      ? Number(q.projection_coef)
      : 0.7;
    if (L_mm > 0 && W_mm > 0) {
      return Math.round((L_mm * W_mm * coef) / 100 * 100) / 100;
    }
    return q.projected_area_cm2 ?? 0;
  };

  const _recomputeTonnage = (q) => {
    const A = Number(q.projected_area_cm2) || 0;
    const N = Math.max(Number(q.num_cavities) || 1, 1);
    const P = Number(q.machine_pressure_bar) || 0;
    // MACH-02 rev Ago 2026: coeficientes 0–1, todos aplicados como (1 + coef).
    const G = q.runner_weight_coef == null ? 0 : Number(q.runner_weight_coef) || 0;
    const S = q.safety_factor == null ? 0 : Number(q.safety_factor) || 0;
    const C = q.mold_complexity_coef == null ? 0.5 : Number(q.mold_complexity_coef) || 0;
    return Math.round((A * N * P * (1 + G) * (1 + S) * (1 + C)) / 1000 * 10) / 10;
  };

  // Campos que disparam recálculo de tonelagem; um subset deles também
  // dispara recálculo da área projectada (length, width, coef). O
  // coeficiente de projecção tem de estar nas duas listas: senão a área
  // muda e a força de fecho fica com o valor anterior.
  const _AREA_DRIVING_FIELDS = new Set([
    'piece_length_mm', 'piece_width_mm', 'projection_coef',
  ]);
  const _TONNAGE_DRIVING_FIELDS = new Set([
    'piece_length_mm',
    'piece_width_mm',
    'projection_coef',
    'machine_pressure_bar',
    'runner_weight_coef',
    'safety_factor',
    'mold_complexity_coef',
    'projected_area_cm2',
    'num_cavities',
  ]);
  const _PIECES_PER_BOX_DRIVING_FIELDS = new Set([
    'box_type_id',
    'piece_length_mm',
    'piece_width_mm',
    'piece_height_mm',
  ]);
  const _BOXES_PER_PALLET_DRIVING_FIELDS = new Set([
    'box_type_id',
    'piece_length_mm',
    'piece_width_mm',
    'piece_height_mm',
    'pieces_per_box',
    'piece_weight_kg',
  ]);

  const updateField = (field, value) => {
    setQuotation((prev) => {
      let next = { ...prev, [field]: value };
      if (_AREA_DRIVING_FIELDS.has(field)) {
        next.projected_area_cm2 = _recomputeProjectedArea(next);
      }
      if (_TONNAGE_DRIVING_FIELDS.has(field)) {
        next.calculated_tonnage_t = _recomputeTonnage(next);
      }
      if (_PIECES_PER_BOX_DRIVING_FIELDS.has(field)) {
        next = applyAutoPiecesPerBox(next, boxTypes);
      }
      if (_BOXES_PER_PALLET_DRIVING_FIELDS.has(field)) {
        next = applyAutoBoxesPerPallet(next, boxTypes);
      }
      return next;
    });
    // Quando o utilizador edita manualmente um campo que tinha sido
    // pré-preenchido pelo desenho 2D, o badge "Sugerido" desaparece — passa
    // a ser um valor "validado" pelo utilizador.
    setSuggestedFields((prev) => {
      const path = `quotation.${field}`;
      if (!prev.has(path)) return prev;
      const next = new Set(prev);
      next.delete(path);
      return next;
    });
  };

  // VA fundição: o input passou a ser o Tempo de ciclo (s/peça). Derivamos
  // shots/h = 3600 / tempo_ciclo para manter intactos os cálculos a jusante.
  const updateCycleTime = (v) => {
    const cyc = Number(v) || 0;
    const shots = cyc > 0 ? Math.round((3600 / cyc) * 100) / 100 : 0;
    setQuotation((prev) => ({
      ...prev,
      cycle_time_s: cyc,
      cycle_time_manual: true,
      shots_per_hour: shots,
    }));
  };

  // Carrega o histórico de versões de preço de uma liga (para o seletor de
  // versão da cotação). Devolve a lista para o chamador escolher a default.
  const loadAlloyVersions = useCallback(async (alloyId) => {
    if (!alloyId) {
      setAlloyVersions([]);
      return [];
    }
    try {
      const res = await configAPI.getAlloyVersions(alloyId);
      setAlloyVersions(res.data || []);
      return res.data || [];
    } catch {
      setAlloyVersions([]);
      return [];
    }
  }, []);

  // Aplica os valores de uma versão de liga à cotação (pin da versão escolhida).
  const applyAlloyVersion = (version) => {
    if (!version) return;
    updateField('alloy_version_id', version.id);
    updateField('base_mb_eur_per_t', version.base_mb_eur_per_t);
    updateField('alloy_premium_eur_per_t', version.premium_eur_per_t);
    updateField('alloy_premium_sell_eur_per_t', version.premium_sell_eur_per_t);
    updateField('paf_percent', version.default_paf_percent);
  };

  // Carrega as versões de preço sempre que a liga escolhida (ou o catálogo)
  // mudar, para popular o seletor de versão da cotação. Colocado após a
  // declaração de loadAlloyVersions para evitar TDZ.
  useEffect(() => {
    const code = quotation?.alloy_code;
    if (!code || !alloys.length) {
      setAlloyVersions([]);
      return;
    }
    const alloy = alloys.find((a) => a.code === code);
    if (alloy) loadAlloyVersions(alloy.id);
  }, [quotation?.alloy_code, alloys, loadAlloyVersions]);

  // Aplica uma lista de Suggestion ao estado local. Driving fields da
  // quotation (length, width) recalculam projected_area_cm2 e tonelagem.
  // Updates da RFQ (part_reference, part_designation) são persistidas
  // imediatamente via PATCH /rfqs/:id porque esses campos não fazem parte
  // do formulário "Save" da quotação.
  // Updates da quotation ficam só em estado local — o utilizador clica
  // "Guardar" no formulário para persistir (consistente com a edição
  // manual dos mesmos campos pela engenharia).
  const applySuggestions = useCallback((list) => {
    if (!Array.isArray(list) || list.length === 0) return;
    const rfqUpdates = {};
    for (const s of list) {
      if (s.infoOnly) continue;
      if (s.section === 'rfq') {
        rfqUpdates[s.field] = s.value;
      }
    }
    const quotationUpdates = quotationUpdatesFromSuggestions(list, alloys);
    if (Object.keys(rfqUpdates).length > 0) {
      setRfq((prev) => (prev ? { ...prev, ...rfqUpdates } : prev));
      rfqAPI.update(id, rfqUpdates).then((res) => {
        setRfq(res.data);
        rfqPersistedRef.current = res.data;
      }).catch((err) => {
        toast.error(formatApiError(err, t('error_saving', lang)));
      });
    }
    if (Object.keys(quotationUpdates).length > 0) {
      setQuotation((prev) => {
        if (!prev) return prev;
        let next = { ...prev, ...quotationUpdates };
        const driving = Object.keys(quotationUpdates);
        if (driving.some((k) => _AREA_DRIVING_FIELDS.has(k))) {
          next.projected_area_cm2 = _recomputeProjectedArea(next);
        }
        if (driving.some((k) => _AREA_DRIVING_FIELDS.has(k) || _TONNAGE_DRIVING_FIELDS.has(k))
            || 'projected_area_cm2' in quotationUpdates) {
          next.calculated_tonnage_t = _recomputeTonnage(next);
        }
        if (driving.some((k) => _PIECES_PER_BOX_DRIVING_FIELDS.has(k))) {
          next = applyAutoPiecesPerBox(next, boxTypes);
        }
        if (driving.some((k) => _BOXES_PER_PALLET_DRIVING_FIELDS.has(k))) {
          next = applyAutoBoxesPerPallet(next, boxTypes);
        }
        return next;
      });
    }
  }, [id, lang, alloys, boxTypes]);

  const markSuggestedFields = useCallback((paths) => {
    if (!Array.isArray(paths) || paths.length === 0) return;
    setSuggestedFields((prev) => {
      const next = new Set(prev);
      for (const p of paths) next.add(p);
      return next;
    });
  }, []);

  const isSuggested = (path) => suggestedFields.has(path);

  const updateFinishingOp = (index, field, value) => {
    setQuotation((prev) => {
      const ops = [...(prev.finishing_operations || [])];
      ops[index] = { ...ops[index], [field]: value };
      return { ...prev, finishing_operations: ops };
    });
  };

  // Trabalhos Extra (Maquinagem CNC, Controlo Dimensional, …) — espelha
  // updateFinishingOp mas opera sobre `extra_works_operations`.
  const updateExtraWorksOp = (index, field, value) => {
    setQuotation((prev) => {
      const ops = [...(prev.extra_works_operations || [])];
      ops[index] = { ...ops[index], [field]: value };
      return { ...prev, extra_works_operations: ops };
    });
  };

  // --- calc_v2 collection helpers ---
  const _editList = (key, mutator) => {
    setQuotation((prev) => {
      // `push`/`splice` return a length or the removed rows — never treat
      // those as the next collection. Only an actual array replaces `list`.
      const list = [...(Array.isArray(prev[key]) ? prev[key] : [])];
      const result = mutator(list);
      const nextList = Array.isArray(result) ? result : list;
      return { ...prev, [key]: nextList };
    });
  };

  const addComponent = () => _editList('components', (l) => {
    l.push({
      row_uid: newRowUid(),
      reference: '', purchase_cost_eur: 0, transport_eur: 0, qty_per_piece: 1,
      scrap_detected: 0, inventory_shrink: 0, supplier_margin: 0, position: l.length,
    });
    return normalisePositions(l);
  });
  const removeComponent = (rowUid) => _editList('components', (l) =>
    normalisePositions(l.filter((row) => row.row_uid !== rowUid)));
  const updateComponent = (rowUid, field, value) => _editList('components', (l) => {
    const idx = l.findIndex((row) => row.row_uid === rowUid);
    if (idx < 0) return l;
    l[idx] = { ...l[idx], [field]: value };
    return l;
  });

  const addSubcontractCandidate = (category) => _editList('subcontract_candidates', (l) => {
    l.push({
      row_uid: newRowUid(),
      category, supplier: '', lot_size: 0,
      purchase_cost_eur: 0, transport_per_pallet_eur: 0, pcs_per_pallet: 0,
      scrap_percent: 0, selected: false, position: l.length,
    });
    return normalisePositions(l);
  });
  const removeSubcontractCandidate = (rowUid) => _editList('subcontract_candidates', (l) =>
    normalisePositions(l.filter((row) => row.row_uid !== rowUid)));
  const updateSubcontractCandidate = (rowUid, field, value) => _editList('subcontract_candidates', (l) => {
    const idx = l.findIndex((row) => row.row_uid === rowUid);
    if (idx < 0) return l;
    l[idx] = { ...l[idx], [field]: value };
    return l;
  });
  const selectSubcontractCandidate = (rowUid) => _editList('subcontract_candidates', (l) => {
    const target = l.find((row) => row.row_uid === rowUid);
    if (!target) return l;
    return l.map((row) => ({
      ...row,
      selected: row.category === target.category ? row.row_uid === rowUid : row.selected,
    }));
  });

  const ensureToolingLines = () => {
    const defaults = [
      { label: 'mould_foundry', cost_eur: 0, margin_percent: 0.15, guarantee_shots: null, position: 0 },
      { label: 'mould_cutting', cost_eur: 0, margin_percent: 0.15, guarantee_shots: null, position: 1 },
      { label: 'gabarit_machining', cost_eur: 0, margin_percent: 0.50, guarantee_shots: null, position: 2 },
      { label: 'gabarit_control', cost_eur: 0, margin_percent: 0.20, guarantee_shots: null, position: 3 },
    ];
    setQuotation((prev) => {
      if (prev?.tooling_lines && prev.tooling_lines.length > 0) return prev;
      return { ...prev, tooling_lines: defaults };
    });
  };
  const updateToolingLine = (i, field, value) => _editList('tooling_lines', (l) => {
    l[i] = { ...l[i], [field]: value };
  });
  // Slugs canónicos das 4 linhas pré-criadas — qualquer label fora desta lista
  // é uma linha custom adicionada pelo utilizador (label livre, removível).
  const TOOLING_CANONICAL_LABELS = useMemo(
    () => new Set(['mould_foundry', 'mould_cutting', 'gabarit_machining', 'gabarit_control']),
    []
  );
  const isCustomToolingLine = (label) => !TOOLING_CANONICAL_LABELS.has(label || '');
  const addToolingLine = () => _editList('tooling_lines', (l) => {
    l.push({
      _key: _newRowKey(), label: '', cost_eur: 0, margin_percent: 0, guarantee_shots: null, position: l.length,
    });
  });
  const removeToolingLine = (i) => _editList('tooling_lines', (l) => {
    l.splice(i, 1);
  });

  // --- Estudos / Desenvolvimentos (informativo, sem impacto no preço) ---
  const addStudyLine = () => _editList('study_lines', (l) => {
    l.push({
      _key: _newRowKey(), label: '', cost_eur: 0, margin_percent: 0, guarantee_shots: null, position: l.length,
    });
  });
  const removeStudyLine = (i) => _editList('study_lines', (l) => {
    l.splice(i, 1);
  });
  const updateStudyLine = (i, field, value) => _editList('study_lines', (l) => {
    l[i] = { ...l[i], [field]: value };
  });

  const processSaveQueue = useCallback(async () => {
    if (saveInFlightRef.current) return;
    const payload = pendingSaveRef.current;
    if (!payload) return;

    if (saveRetryTimerRef.current) {
      clearTimeout(saveRetryTimerRef.current);
      saveRetryTimerRef.current = null;
    }

    pendingSaveRef.current = null;
    saveInFlightRef.current = true;
    setAutoSaveStatus('saving');

    // Waiters registados ANTES deste pedido pertencem a este payload; os que
    // chegarem durante o voo pertencem ao próximo envio.
    const waiters = saveWaitersRef.current;
    saveWaitersRef.current = [];

    const payloadWithRevision = {
      ...payload,
      revision: revisionRef.current,
    };
    let failed = false;

    try {
      const res = await quotationAPI.update(id, payloadWithRevision);
      if (res.data.calc_snapshot) setCalcResult(res.data.calc_snapshot);
      if (res.data.revision != null) {
        revisionRef.current = res.data.revision;
      }
      // Propaga também o pnl_audit_snapshot recalculado pelo backend — é ele
      // que alimenta o card "Validação de preço" e o gate do Comercial. Sem
      // isto, o card ficava "Auditoria indisponível" até um F5 da página.
      // (Campo excluído de serializeQuotationForCompare — não dispara auto-save.)
      setQuotation((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        if (res.data.revision != null) next.revision = res.data.revision;
        if (res.data.pnl_audit_snapshot !== undefined) {
          next.pnl_audit_snapshot = res.data.pnl_audit_snapshot;
        }
        // Peças/caixa é derivado no servidor (caixa + dimensões). Se o
        // utilizador não mexeu no campo durante o pedido, aplica o valor
        // calculado — o payload enviado podia ainda ir a 0.
        if (
          payload.box_type_id != null
          && res.data.pieces_per_box != null
          && Number(prev.pieces_per_box ?? 0) === Number(payload.pieces_per_box ?? 0)
        ) {
          next.pieces_per_box = res.data.pieces_per_box;
        }
        if (
          payload.box_type_id != null
          && res.data.boxes_per_pallet != null
          && Number(prev.boxes_per_pallet ?? 0) === Number(payload.boxes_per_pallet ?? 0)
        ) {
          next.boxes_per_pallet = res.data.boxes_per_pallet;
        }
        return next;
      });
      // CRÍTICO: a baseline de "persistido" é o payload REALMENTE enviado —
      // nunca o estado actual, que pode já conter edições feitas durante o
      // pedido. Se marcássemos o estado actual como persistido, essas edições
      // (linhas adicionadas/removidas durante o voo) nunca seriam gravadas e
      // qualquer reload restauraria o estado antigo do servidor.
      lastPersistedQuotationRef.current = serializeQuotationForCompare(payload);
      setAutoSaveStatus('saved');
      waiters.forEach(({ resolve }) => resolve());
    } catch (err) {
      failed = true;
      setAutoSaveStatus('error');
      if (err.response?.status === 409) {
        toast.error(
          formatApiError(
            err,
            'A cotação foi alterada por outra operação. Atualize os dados e tente novamente.',
          ),
        );
        // Revisão desatualizada (outra aba/pedido concorrente): sincroniza só a
        // revisão actual do servidor; o estado local mais recente é reenviado
        // no retry com a revisão correcta.
        try {
          const cur = await quotationAPI.get(id);
          if (cur.data?.revision != null) revisionRef.current = cur.data.revision;
        } catch { /* mantém a revisão local; o retry voltará a tentar */ }
      }
      waiters.forEach(({ reject }) => reject(err));
      // Repõe o payload por gravar, excepto se entretanto chegou um mais recente.
      if (!pendingSaveRef.current) {
        pendingSaveRef.current = payload;
      }
    } finally {
      saveInFlightRef.current = false;
      // Edições feitas durante o pedido: se não há pending explícito mas o
      // estado actual difere da baseline, enfileira-o.
      if (
        !pendingSaveRef.current
        && quotationRef.current
        && serializeQuotationForCompare(quotationRef.current) !== lastPersistedQuotationRef.current
      ) {
        pendingSaveRef.current = JSON.parse(JSON.stringify(quotationRef.current));
      }
      if (pendingSaveRef.current) {
        if (failed) {
          // Retry com atraso — evita loop apertado de PUTs em erros persistentes.
          saveRetryTimerRef.current = setTimeout(() => {
            saveRetryTimerRef.current = null;
            processSaveQueue();
          }, 2500);
        } else {
          processSaveQueue();
        }
      }
    }
  }, [id]);

  const enqueueQuotationSave = useCallback((payload, { wait = false } = {}) => {
    pendingSaveRef.current = JSON.parse(JSON.stringify(payload));
    const promise = wait
      ? new Promise((resolve, reject) => { saveWaitersRef.current.push({ resolve, reject }); })
      : null;
    if (!saveInFlightRef.current) {
      processSaveQueue();
    }
    return promise;
  }, [processSaveQueue]);

  const flushPendingQuotation = async ({ requireDirty = false } = {}) => {
    if (baseMbCommentMissing) {
      toast.error(t('base_mb_comment_required', lang));
      return false;
    }
    if (baseMbCommentTooShort) {
      toast.error(t('base_mb_comment_min', lang).replace('{n}', BASE_MB_COMMENT_MIN_LEN));
      return false;
    }
    const cur = quotationRef.current;
    if (!cur) return true;
    if (
      requireDirty
      && serializeQuotationForCompare(cur) === lastPersistedQuotationRef.current
    ) {
      return true;
    }
    try {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      await enqueueQuotationSave(cur, { wait: true });
      return true;
    } catch (err) {
      toast.error(formatApiError(err, t('error_saving', lang)));
      return false;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await flushPendingQuotation();
      if (!ok) return;
      toast.success(t('saved_and_recalculated', lang));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = async () => {
    if (isQuotationSavable) {
      const ok = await flushPendingQuotation({ requireDirty: true });
      if (!ok) return;
    }
    navigate('/rfqs');
  };

  const scheduleAutoSave = useCallback((payload) => {
    enqueueQuotationSave(payload);
  }, [enqueueQuotationSave]);

  // Ao desmontar a página, cancela qualquer retry agendado da fila de gravação.
  useEffect(() => () => {
    if (saveRetryTimerRef.current) {
      clearTimeout(saveRetryTimerRef.current);
      saveRetryTimerRef.current = null;
    }
  }, []);

  // Duplica a RFQ/cotação atual numa nova RFQ em preparação e navega para ela.
  const handleDuplicate = async () => {
    if (duplicating) return;
    setDuplicating(true);
    try {
      const res = await rfqAPI.duplicate(id);
      toast.success(t('duplicated_toast', lang));
      navigate(`/rfqs/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, t('error_saving', lang)));
    } finally {
      setDuplicating(false);
    }
  };

  // Persist RFQ metadata changes (deadline/engineer) without touching the quotation.
  const saveRfqMeta = async (patch) => {
    setMetaSaving(true);
    try {
      const res = await rfqAPI.update(id, patch);
      rfqPersistedRef.current = res.data;
      setRfq((prev) => {
        const next = { ...res.data, ...buildG2MetaPatch(prev, res.data) };
        rfqRef.current = next;
        return next;
      });
      toast.success(t('rfq_meta_saved', lang));
      return true;
    } catch (err) {
      toast.error(formatApiError(err, t('error_saving', lang)));
      return false;
    } finally {
      setMetaSaving(false);
    }
  };

  // Diretor Técnico atribui o engenheiro responsável (awaiting_engineering_assignment → in_analysis).
  const handleAssignEngineer = async () => {
    if (!assignEngineerId) return;
    setMetaSaving(true);
    try {
      await rfqAPI.assignEngineer(id, parseInt(assignEngineerId, 10));
      toast.success(t('engineer_assigned_ok', lang));
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, t('error_saving', lang)));
    } finally {
      setMetaSaving(false);
    }
  };

  const openDialog = (config) => {
    setDialogComment('');
    setDialogRisk('medium');
    setDialogSendEmail(true);
    // Para request_client_info: pré-selecciona o primeiro role com email
    // configurado (ordem: comercial → projeto → qualidade → logistica). Se
    // nenhum estiver definido, fallback para input livre com email_primary.
    if (config?.action === 'request_client_info') {
      const c = rfq?.client || {};
      // Destinatários vêm da tabela de contactos da equipa do cliente.
      // Pré-selecciona o primeiro contacto com email; caso não haja, cai
      // sobre o email principal legado (clientes antigos).
      const contacts = (c.email_contacts || []).filter((x) => (x.email || '').trim());
      setDialogRecipientRole('custom');
      if (contacts.length) {
        setDialogEmailTo(contacts[0].email);
      } else {
        setDialogEmailTo(c.email_primary || '');
      }
    } else {
      setDialogRecipientRole('');
      setDialogEmailTo('');
    }
    setWorkflowDialog(config);
  };

  const runDialogAction = async () => {
    if (!workflowDialog) return;
    const { action, minChars = 0, needsRisk = false } = workflowDialog;
    if (minChars && dialogComment.trim().length < minChars) {
      toast.error(t('justification_min_chars', lang));
      return;
    }
    if (needsRisk && !['low', 'medium', 'high'].includes(dialogRisk)) {
      toast.error(t('risk_required', lang));
      return;
    }
    setDialogBusy(true);
    try {
      switch (action) {
        case 'strategic_reject':
          await rfqAPI.strategicReject(id, dialogComment);
          toast.success(t('strategic_rejected_toast', lang));
          break;
        case 'request_client_info': {
          const isCustom = !dialogRecipientRole || dialogRecipientRole === 'custom';
          // Se o utilizador escolheu um role departamental, o backend resolve
          // o email a partir do Client; o input livre só é usado em modo
          // 'custom' (fallback ad-hoc).
          const recipientList = dialogSendEmail && isCustom
            ? (dialogEmailTo || '')
                .split(/[,;]/)
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
          if (dialogSendEmail && isCustom && recipientList.length === 0) {
            toast.warning(t('client_info_no_email_toast', lang));
          }
          const res = await rfqAPI.requestClientInfo(id, dialogComment, {
            send_email: dialogSendEmail,
            recipient_role: !isCustom ? dialogRecipientRole : null,
            recipients: recipientList.length ? recipientList : null,
          });
          const emailInfo = res?.data?.email || {};
          if (dialogSendEmail && emailInfo.sent) {
            toast.success(t('client_info_requested_with_email_toast', lang));
          } else if (dialogSendEmail && !emailInfo.sent) {
            const reason = emailInfo.reason || 'unknown';
            const detail = emailInfo.error
              ? `${reason}: ${emailInfo.error}`
              : reason;
            toast.warning(
              `${t('client_info_requested_no_email_toast', lang)} (${detail})`
            );
          } else {
            toast.success(t('client_info_requested_toast', lang));
          }
          break;
        }
        case 'return_to_commercial':
          await rfqAPI.returnToCommercial(id, dialogComment);
          toast.success(t('returned_to_commercial_toast', lang));
          break;
        case 'submit_to_commercial_margins':
          await rfqAPI.submitToCommercialMargins(id, dialogRisk, dialogComment);
          toast.success(t('submitted_commercial_margins_toast', lang));
          break;
        case 'submit_final_decision': {
          const flushed = await flushPendingQuotation({ requireDirty: true });
          if (!flushed) return;
          const res = await rfqAPI.submitFinalDecision(id, dialogComment);
          const next = res?.data?.next_state || '';
          if (next === 'awaiting_direction') {
            toast.success(t('escalated_to_administration_toast', lang));
          } else {
            toast.success(
              res?.data?.schreder_warning_sent
                ? t('approved_with_admin_warning_toast', lang)
                : t('approved_toast', lang)
            );
          }
          break;
        }
        case 'reopen_from_negotiation':
          await rfqAPI.reopenFromNegotiation(id, dialogComment);
          toast.success(t('reopened_negotiation_toast', lang));
          break;
        case 'approve_exception':
          await rfqAPI.validate(id, { decision: 'approved', comment: dialogComment });
          toast.success(t('approved_toast', lang));
          break;
        case 'reject_final':
          await rfqAPI.validate(id, { decision: 'rejected', comment: dialogComment });
          toast.success(t('rejected_toast', lang));
          break;
        default:
          break;
      }
      setWorkflowDialog(null);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, t('error_operation', lang)));
    } finally {
      setDialogBusy(false);
    }
  };

  const runDirect = async (fn, successKey) => {
    try {
      await fn();
      toast.success(t(successKey, lang));
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, t('error_operation', lang)));
    }
  };

  const handleSubmitEngineering = async () => {
    const persisted = rfqPersistedRef.current || {};
    const patch = buildG2MetaPatch(rfqRef.current || rfq, persisted);
    if (Object.keys(patch).length > 0) {
      const ok = await saveRfqMeta(patch);
      if (!ok) return;
    }
    await runDirect(() => rfqAPI.submitToEngineering(id), 'submitted_engineering');
  };

  const handleAddCrossProfileNote = async () => {
    const text = crossNoteText.trim();
    if (!text) return;
    setCrossNoteBusy(true);
    try {
      await rfqAPI.addNote(id, { text, section: 'cross_profile' });
      setCrossNoteText('');
      const notesRes = await rfqAPI.listNotes(id);
      setNotes(notesRes.data || []);
      toast.success(t('cross_profile_note_added', lang));
    } catch (err) {
      toast.error(formatApiError(err, t('error_operation', lang)));
    } finally {
      setCrossNoteBusy(false);
    }
  };

  const handleMarkClientInfoReceived = () =>
    runDirect(() => rfqAPI.markClientInfoReceived(id), 'client_info_received_toast');

  const handleMarkOfferSent = () =>
    runDirect(() => rfqAPI.markOfferSent(id), 'offer_sent_toast');

  const openOfferDialog = () => {
    const offerLang = ['en', 'fr', 'pt'].includes(lang) ? lang : 'en';
    setOfferForm({
      observations: '',
      validity_days: '',
      specific_conditions: '',
      payment_terms_parts: '',
      payment_terms_tooling: '',
      planning_first_article_weeks: '12',
      planning_first_series_weeks: '2',
      planning_supplying_weeks: '8',
      planning_supplying_forecast_weeks: '6',
      language: offerLang,
    });
    setOfferDialogOpen(true);
  };

  const handleGenerateOffer = async () => {
    setOfferBusy(true);
    try {
      // Converte strings vazias em null/undefined para o backend cair sobre
      // os defaults da configuração global em vez de receber 0.
      const numOrNull = (v) => {
        const s = (v ?? '').toString().trim();
        if (s === '') return null;
        const n = parseInt(s, 10);
        return Number.isFinite(n) ? n : null;
      };
      const payload = {
        observations: offerForm.observations || '',
        validity_days: numOrNull(offerForm.validity_days),
        specific_conditions: offerForm.specific_conditions || '',
        payment_terms_parts: offerForm.payment_terms_parts || '',
        payment_terms_tooling: offerForm.payment_terms_tooling || '',
        planning_first_article_weeks: numOrNull(offerForm.planning_first_article_weeks),
        planning_first_series_weeks: numOrNull(offerForm.planning_first_series_weeks),
        planning_supplying_weeks: numOrNull(offerForm.planning_supplying_weeks),
        planning_supplying_forecast_weeks: numOrNull(offerForm.planning_supplying_forecast_weeks),
        language: offerForm.language || 'en',
      };
      const res = await offerAPI.generate(id, payload);
      setOffers((prev) => [res.data, ...prev]);
      setOfferDialogOpen(false);
      toast.success(t('offer_generated_toast', lang));
    } catch (err) {
      toast.error(formatApiError(err, t('offer_generate_error', lang)));
    } finally {
      setOfferBusy(false);
    }
  };

  const handleDownloadOffer = async (offer) => {
    try {
      const blob = await offerAPI.download(id, offer.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Offer_${rfq?.reference_number || id}_v${offer.version}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('offer_download_error', lang));
    }
  };

  const openOutcomeDialog = (kind) => {
    setOutcomeDialog(kind);
    setOutcomeNotes('');
    setOutcomeLossReason('price');
  };

  const handleConfirmOutcome = async () => {
    if (!outcomeDialog) return;
    setOutcomeBusy(true);
    try {
      const payload = { result: outcomeDialog, notes: outcomeNotes };
      if (outcomeDialog === 'lost') payload.loss_reason = outcomeLossReason;
      await rfqAPI.updateTracking(id, payload);
      const successKey =
        outcomeDialog === 'won'
          ? 'outcome_won_toast'
          : outcomeDialog === 'lost'
            ? 'outcome_lost_toast'
            : 'outcome_in_negotiation_toast';
      toast.success(t(successKey, lang));
      setOutcomeDialog(null);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, t('error_operation', lang)));
    } finally {
      setOutcomeBusy(false);
    }
  };

  const handleUnlockForEdit = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('unlock_confirm', lang))) return;
    try {
      await rfqAPI.unlockForEdit(id);
      toast.success(t('edit_rfq_version', lang));
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, t('error_operation', lang)));
    }
  };

  const handleLockEdit = async () => {
    setLockBusy(true);
    try {
      // Persist any pending edits to the quotation BEFORE sealing, otherwise
      // the backend sees DB values identical to the baseline and skips vN.
      if (quotation) {
        const ok = await flushPendingQuotation();
        if (!ok) return;
      }
      await rfqAPI.lockEdit(id, lockComment);
      toast.success(t('lock_edit_version', lang));
      setLockDialogOpen(false);
      setLockComment('');
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, t('error_operation', lang)));
    } finally {
      setLockBusy(false);
    }
  };

  const handleOpenVersion = async (versionId) => {
    setLoadingVersion(true);
    try {
      const res = await rfqAPI.getVersion(id, versionId);
      setVersionDetail(res.data);
    } catch (err) {
      toast.error(formatApiError(err, t('error_operation', lang)));
    } finally {
      setLoadingVersion(false);
    }
  };

  // Role-based visibility. Admin is an override that can act as any profile.
  const userRole = user?.role_name || '';
  const isAdminUser = Boolean(user?.is_admin);
  const isCommercialUser = isAdminUser || userRole === 'Comercial';
  const isEngineerUser = isAdminUser || userRole === 'Engenharia';
  const isDirectorUser = isAdminUser || userRole === 'Diretor Técnico de Engenharia';
  // G5 (exception) actor: Administração profile or system admin. The legacy
  // 'Direcção' role is migrated away on backend start, so we only need to
  // accept its canonical spellings here.
  const isAdministrationUser =
    isAdminUser ||
    userRole === 'Administração' ||
    userRole === 'Administracao';

  // FE-04: gate por PERMISSÃO (não por role) — alinhado com a redacção da API.
  const canViewMargin = isAdminUser || Boolean(user?.permissions?.rfq_view_margin);

  const canUnlockEng = canUnlockEdit({ rfq, isEngineerResponsible });
  const canLockEng = canLockEdit({ rfq, isEngineerResponsible });

  const canEditTechnicalQuotation =
    rfq &&
    ((rfq.status === 'in_preparation' && isCommercialUser) ||
      (rfq.status === 'in_analysis' && rfq.edit_mode && isEngineerUser));

  // MACH-02 — máquina e área projectada são auto-preenchidas pela fórmula.
  // Apenas a engenharia (em in_analysis com edit_mode) pode override
  // manualmente. O comercial nunca edita estes dois campos, mesmo na
  // fase em que tem o controlo do resto da cotação técnica.
  const canEditMachineAndArea = Boolean(
    rfq && rfq.status === 'in_analysis' && rfq.edit_mode && isEngineerUser,
  );

  const canEditMarginsQuotation = Boolean(rfq && rfq.status === 'awaiting_commercial_margins' && isCommercialUser);

  // WF-01: o gate intermédio de Logística/Financeiro foi removido. Embalagem e
  // transporte passam a ser editados na fase técnica (Engenharia/Comercial-prep).
  const canEditPackagingTransport = Boolean(canEditTechnicalQuotation);

  const isQuotationSavable = Boolean(
    rfq && (canEditTechnicalQuotation || canEditMarginsQuotation),
  );

  // Base MB override (edição manual na cotação). O valor canónico vem da
  // versão da liga escolhida (ou, em fallback, do catálogo da liga). Quando o
  // valor guardado na cotação diverge, é um override e exige comentário.
  const _selectedAlloyVersion = (alloyVersions || []).find(
    (v) => v.id === quotation?.alloy_version_id,
  );
  const _canonicalBaseMb = _selectedAlloyVersion
    ? _selectedAlloyVersion.base_mb_eur_per_t
    : (alloys || []).find((a) => a.code === quotation?.alloy_code)?.base_mb_eur_per_t;
  const isBaseMbOverride =
    _canonicalBaseMb != null &&
    quotation?.base_mb_eur_per_t != null &&
    Number(quotation.base_mb_eur_per_t) !== Number(_canonicalBaseMb);
  const _baseMbCommentLen = (quotation?.base_mb_override_comment || '').trim().length;
  const baseMbCommentMissing = isBaseMbOverride && _baseMbCommentLen === 0;
  // Motivo obrigatório deve ter pelo menos BASE_MB_COMMENT_MIN_LEN caracteres.
  const baseMbCommentTooShort =
    isBaseMbOverride && _baseMbCommentLen > 0 && _baseMbCommentLen < BASE_MB_COMMENT_MIN_LEN;
  const baseMbCommentInvalid = baseMbCommentMissing || baseMbCommentTooShort;

  // VA fundição — tempo de ciclo (s/peça) e informativos derivados.
  // Para cotações antigas sem cycle_time_s guardado, deriva-se de shots/h.
  const _cycleTimeS =
    quotation?.cycle_time_s ||
    (quotation?.shots_per_hour ? Math.round((3600 / quotation.shots_per_hour) * 100) / 100 : 0);
  const shotsPerDay = _cycleTimeS > 0 ? Math.round(86400 / _cycleTimeS) : 0;
  const piecesPerDay = Math.round(
    shotsPerDay * (Number(quotation?.trs_percent) || 0) * (Number(quotation?.num_cavities) || 1),
  );
  const piecesPerDayNet = piecesPerDayWithoutScrap(
    piecesPerDay,
    quotation?.scrap_rate_foundry_percent,
  );

  // Sugestão de tempo de ciclo a partir do histórico (Admin → Tempos de
  // ciclo). Requer peso da peça e máquina (auto-seleccionada ou manual);
  // peso c/ gito = peso peça + peso gito (card VM). Debounce para não
  // chamar a API a cada tecla.
  const [cycleSuggestion, setCycleSuggestion] = useState(null);
  const _suggPieceW = Number(quotation?.piece_weight_kg) || 0;
  const _suggRunnerW = Number(quotation?.runner_weight_kg) || 0;
  const _suggMachineName = (() => {
    if (quotation?.machine_override && quotation?.machine_id) {
      return machines.find((m) => m.id === quotation.machine_id)?.name || '';
    }
    const sel = calcResult?.machine?.selected_machine;
    return sel?.name || machines.find((m) => m.id === quotation?.machine_id)?.name || '';
  })();
  useEffect(() => {
    if (!(_suggPieceW > 0) || !_suggMachineName) {
      setCycleSuggestion(null);
      return undefined;
    }
    const handle = setTimeout(() => {
      configAPI
        .suggestCycleTime({
          part_weight_kg: _suggPieceW,
          part_weight_with_runner_kg: _suggPieceW + _suggRunnerW,
          machine_name: _suggMachineName,
        })
        .then((res) =>
          setCycleSuggestion(res.data?.suggested_cycle_time_s != null ? res.data : null),
        )
        .catch(() => setCycleSuggestion(null));
    }, 600);
    return () => clearTimeout(handle);
  }, [_suggPieceW, _suggRunnerW, _suggMachineName]);

  // Protege contra perda de dados: se o utilizador recarregar/fechar a página
  // enquanto ainda há alterações por gravar (ex.: apagou linhas e o auto-save
  // com debounce de 1,2s ainda não disparou), o browser pede confirmação. Sem
  // isto, um refresh dentro dessa janela reverteria as edições/remoções.
  useEffect(() => {
    const handler = (e) => {
      const cur = quotationRef.current;
      if (!cur) return;
      if (serializeQuotationForCompare(cur) === lastPersistedQuotationRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Auto-save com debounce. Dispara sempre que ``quotation`` muda em
  // relação ao último estado persistido — desde que (a) a cotação seja
  // editável pelo utilizador actual e (b) a mudança não venha do
  // servidor (carregamento inicial ou resposta a um save anterior).
  useEffect(() => {
    if (!quotation) return undefined;
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return undefined;
    }
    if (!isQuotationSavable) return undefined;
    const snapshotJson = serializeQuotationForCompare(quotation);
    if (snapshotJson === lastPersistedQuotationRef.current) return undefined;

    // Bloqueia o auto-save enquanto o motivo obrigatório do override do Base MB
    // estiver em falta ou for demasiado curto (o backend recusaria com 400).
    if (baseMbCommentInvalid) {
      setAutoSaveStatus('pending');
      return undefined;
    }

    setAutoSaveStatus('pending');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      scheduleAutoSave(quotation);
    }, 1200);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [quotation, isQuotationSavable, scheduleAutoSave, baseMbCommentInvalid]);

  const canAttachDocuments =
    rfq &&
    ((rfq.status === 'in_preparation' && isCommercialUser) ||
      (rfq.status === 'in_analysis' && rfq.edit_mode && isEngineerUser));

  const canEditCommercialMarginFields =
    isAdminUser ||
    (rfq?.status === 'awaiting_commercial_margins' && isCommercialUser) ||
    (rfq?.status === 'in_preparation' && isCommercialUser);

  const canDelete =
    rfq?.status === 'in_preparation' &&
    user &&
    (user.is_admin || rfq.created_by === user.id);

  // Duplicação disponível quando a RFQ chegou ao final do percurso
  // (aprovada, oferta enviada, ganha ou perdida) e o utilizador pode criar RFQs.
  const canDuplicate =
    rfq &&
    ['approved', 'offer_sent', 'won', 'lost'].includes(rfq.status) &&
    (isAdminUser || Boolean(user?.permissions?.rfq_create));

  // Live bucket preview: mirrors the backend pricing block so users see the
  // impact of manual commercial margins immediately (without having to save).
  // The base per-piece costs come from the last server-side calc; the margins
  // come straight from the form state, so unsaved margin edits still render.
  const liveBuckets = useMemo(() => {
    if (!calcResult) return null;
    const vm = Number(calcResult.vm_eur_per_piece || 0);
    const vaFoundry = Number(calcResult.va_foundry_eur_per_piece || 0);
    const vaFinishing = Number(calcResult.va_finishing_eur_per_piece || 0);
    const vaExtra = Number(calcResult.va_extra_works_eur_per_piece || 0);
    const vaInternal = vaFoundry + vaFinishing;
    const packaging = Number(calcResult.packaging_eur_per_piece || 0);
    const transport = Number(calcResult.transport_eur_per_piece || 0);
    const components = Number(calcResult.components_eur_per_piece || 0);
    const subcontract = Number(calcResult.subcontract_eur_per_piece || 0);

    // Maio 2026: motor unificado — todas as margens vêm da própria cotação;
    // default 0 quando a célula está vazia. ``margin_extra_works`` herda
    // a margem interna se não for definida (única excepção, ergonómica).
    const effMi = Number(quotation?.margin_internal_ops_percent ?? 0) || 0;
    const effMp = Number(quotation?.margin_packaging_percent ?? 0) || 0;
    const effMt = Number(quotation?.margin_transport_percent ?? 0) || 0;
    const effMs = Number(quotation?.margin_subcontract_percent ?? 0) || 0;
    const effMc = Number(quotation?.margin_components_percent ?? 0) || 0;
    const meRaw = quotation?.margin_extra_works_percent;
    const effMe = meRaw == null || meRaw === '' ? effMi : Number(meRaw);

    const bucketVm = vm;
    // Sub-buckets individualizados — espelham as Margens segmentadas e
    // alimentam as 4 linhas do card "Preço de Venda (por bucket)".
    const bucketVaInternal = vaInternal * (1 + effMi);
    const bucketExtraWorks = vaExtra * (1 + effMe);
    const bucketPackaging = packaging * (1 + effMp);
    const bucketTransport = transport * (1 + effMt);
    const bucketVaPackTr =
      bucketVaInternal + bucketExtraWorks + bucketPackaging + bucketTransport;
    const bucketComponents = components * (1 + effMc);
    const bucketSubcontract = subcontract * (1 + effMs);
    const salePrice = bucketVm + bucketVaPackTr + bucketComponents + bucketSubcontract;
    const productionCost =
      vm + vaInternal + vaExtra + packaging + transport + components + subcontract;
    const marginEur = salePrice - productionCost;
    const denom = salePrice - bucketVm;
    const marginGlobalPercent = denom > 0 ? marginEur / denom : 0;

    // KPI VA anual esperado — receita anualizada gerada apenas pelo VA
    // interno (Fundição + Acabamento) já com a margem aplicada. Mantido
    // alinhado com a fórmula do backend (engine.py: expected_annual_va_eur).
    const annualVolume = Number(rfq?.annual_volume || 0);
    // Arredondar a 2 casas como o backend (`kpis.expected_annual_va_eur`) para
    // o total bater com o snapshot gravado após guardar.
    const expectedAnnualVa = annualVolume > 0
      ? Math.round(bucketVaInternal * annualVolume * 100) / 100
      : 0;

    return {
      segmented: true,
      margins: {
        va_internal: effMi,
        packaging: effMp,
        transport: effMt,
        extra_works: effMe,
        subcontract: effMs,
        components: effMc,
      },
      buckets: { vm: 0, components: effMc, subcontract: effMs },
      bucket_vm: bucketVm,
      bucket_va_pack_trans: bucketVaPackTr,
      bucket_va_internal: bucketVaInternal,
      bucket_extra_works: bucketExtraWorks,
      bucket_packaging: bucketPackaging,
      bucket_transport: bucketTransport,
      bucket_components: bucketComponents,
      bucket_subcontract: bucketSubcontract,
      sale_price: salePrice,
      production_cost: productionCost,
      margin_eur: marginEur,
      margin_global_percent: marginGlobalPercent,
      expected_annual_va_eur: expectedAnnualVa,
    };
  }, [
    calcResult,
    rfq?.annual_volume,
    quotation?.margin_internal_ops_percent,
    quotation?.margin_packaging_percent,
    quotation?.margin_transport_percent,
    quotation?.margin_extra_works_percent,
    quotation?.margin_subcontract_percent,
    quotation?.margin_components_percent,
  ]);

  const userDisplayMap = useMemo(() => {
    const m = {};
    for (const u of users || []) {
      if (u?.id != null) m[u.id] = u;
    }
    if (user?.id != null) m[user.id] = user;
    if (rfq?.engineer?.id != null) {
      m[rfq.engineer.id] = rfq.engineer;
    }
    for (const v of versions || []) {
      if (v?.author?.id != null) m[v.author.id] = v.author;
    }
    if (versionDetail?.author?.id != null) {
      m[versionDetail.author.id] = versionDetail.author;
    }
    return m;
  }, [users, user, rfq?.engineer, versions, versionDetail]);

  const engineerDisplayName =
    rfq?.engineer?.full_name ||
    rfq?.engineer?.username ||
    (rfq?.engineering_responsible != null
      ? userDisplayMap[rfq.engineering_responsible]?.full_name ||
        userDisplayMap[rfq.engineering_responsible]?.username
      : null);

  const handleDelete = async () => {
    if (!rfq) return;
    const confirmMsg = t('delete_rfq_confirm', lang).replace('{ref}', rfq.reference_number);
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmMsg)) return;
    try {
      await rfqAPI.remove(id);
      toast.success(t('rfq_deleted_toast', lang));
      navigate('/rfqs');
    } catch (err) {
      toast.error(formatApiError(err, t('error_operation', lang)));
    }
  };

  const reloadDocuments = useCallback(async () => {
    try {
      const docsRes = await rfqAPI.listDocuments(id);
      setDocuments(docsRes.data);
    } catch {
      // silencioso — o toast já é apresentado pelo caller que provoca o reload
    }
  }, [id]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await rfqAPI.uploadDocument(id, file);
      toast.success(t('doc_attached', lang));
      await reloadDocuments();
      e.target.value = '';
    } catch {
      toast.error(t('error_attaching_doc', lang));
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const blob = await rfqAPI.downloadDocument(id, doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err, t('error_download_doc', lang)));
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }
  if (!rfq) return <div className="text-destructive">{t('rfq_not_found', lang)}</div>;

  const transportInc = (quotation?.transport_incoterm || 'exwork').toLowerCase();
  const showTransportKmFields = transportInc === 'ddp' || transportInc === 'dap';
  // Transporte (€/lote) e Peças por lote são entradas manuais usadas apenas
  // quando o cliente cobra por lote (incoterm "Outros"). Em Ex Works, DDP
  // e DAP o custo de transporte é derivado de km × €/km × (1/peças/viagem)
  // ou está incluído no preço CIF, portanto escondemos os campos para
  // evitar duplicação de inputs.
  const showTransportLotFields = transportInc !== 'exwork' && transportInc !== 'ddp' && transportInc !== 'dap';

  const CostRow = ({ label, value, strong = false, accent = false }) => (
    <div
      className={`flex items-center justify-between border-b border-dashed border-border py-1.5 text-sm last:border-0 ${
        strong ? 'pt-2 font-semibold' : ''
      }`}
    >
      <span className={strong ? '' : 'text-muted-foreground'}>{label}</span>
      <span className={`tnum ${accent ? 'text-primary' : ''}`}>{value}</span>
    </div>
  );

  return (
    <CardFoldProvider lang={lang}>
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Button id="rfq-back" variant="ghost" size="sm" onClick={handleBack} className="-ml-3 mb-2">
            <ArrowLeft className="h-4 w-4" /> {t('back', lang)}
          </Button>
          <p className="page-kicker">{t('rfqs', lang)}</p>
          <h1 className="page-title mt-1">{rfq.reference_number}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {rfq.part_designation} — <span className="font-medium text-foreground">{rfq.client?.name}</span>
          </p>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <Badge variant={statusBadgeVariant(rfq.status)} className="px-3 py-1 text-sm">
            {t(rfq.status, lang)}
          </Badge>
          {rfq.deadline && (
            <div className="text-xs text-muted-foreground">
              {t('deadline', lang)}: <span className="tnum">{formatDate(rfq.deadline, lang)}</span>
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            {t('engineer_responsible_label', lang)}:{' '}
            <span className="font-medium text-foreground">
              {engineerDisplayName || t('engineer_not_assigned', lang)}
            </span>
          </div>
          {isOverdue(rfq) && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" /> {t('sla_overdue_badge', lang)}
            </Badge>
          )}
          {awaitingClient && (
            <Badge variant="warning" className="gap-1">
              <MailQuestion className="h-3 w-3" /> {t('awaiting_client_flag', lang)}
            </Badge>
          )}
          {rfq.status === 'in_analysis' && quotation?.calc_version > 1 && (
            <Badge variant="info" className="gap-1">
              <Undo2 className="h-3 w-3" /> {t('rework_version_badge', lang)}{quotation.calc_version}
            </Badge>
          )}
        </div>
      </div>

      {/* Always-available actions: save & duplicate */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          {isQuotationSavable && (
            <Button onClick={handleSave} disabled={saving} variant="success" size="sm">
              <Save className="h-4 w-4" />
              {saving ? t('saving_wait', lang) : t('save_quotation', lang)}
            </Button>
          )}
          {isQuotationSavable && autoSaveStatus !== 'idle' && (
            <span
              className={`text-xs font-medium ${
                autoSaveStatus === 'error'
                  ? 'text-destructive'
                  : autoSaveStatus === 'saved'
                  ? 'text-emerald-600'
                  : 'text-muted-foreground'
              }`}
              aria-live="polite"
            >
              {autoSaveStatus === 'pending' && t('autosave_pending', lang)}
              {autoSaveStatus === 'saving' && t('autosave_saving', lang)}
              {autoSaveStatus === 'saved' && t('autosave_saved', lang)}
              {autoSaveStatus === 'error' && t('autosave_error', lang)}
            </span>
          )}
          {canDelete && (
            <Button onClick={handleDelete} variant="destructive" size="sm">
              <Trash2 className="h-4 w-4" /> {t('delete', lang)}
            </Button>
          )}
          {canDuplicate && (
            <Button onClick={handleDuplicate} disabled={duplicating} variant="outline" size="sm">
              <Copy className="h-4 w-4" /> {duplicating ? t('duplicating', lang) : t('duplicate', lang)}
            </Button>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {rfq.status === 'approved' && isCommercialUser && (
              <Button onClick={handleMarkOfferSent} size="sm">
                <Mail className="h-4 w-4" /> {t('mark_offer_sent', lang)}
              </Button>
            )}
            <CardFoldToolbar lang={lang} />
          </div>
        </CardContent>
      </Card>

      {rfq.status !== 'in_preparation' && (
        <CollapsibleCard
          id="g2_project_info"
          className="border-info/40"
          headerClassName="pb-2"
          title={(
            <>
              <FileText className="h-4 w-4 text-info" /> {t('gate_g2', lang)} — {t('g2_project_info', lang)}
            </>
          )}
          contentClassName="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4"
        >
            <div>
              <div className="text-xs text-muted-foreground">{t('project_name', lang)}</div>
              <div className="font-medium">{rfq.project_name || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t('plan_number', lang)}</div>
              <div className="font-medium">{rfq.plan_number || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t('plan_revision', lang)}</div>
              <div className="font-medium">{rfq.plan_revision || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t('plan_revision_date', lang)}</div>
              <div className="font-medium">
                {rfq.plan_revision_date ? rfq.plan_revision_date.substring(0, 10) : '—'}
              </div>
            </div>
        </CollapsibleCard>
      )}

      {rfq.status === 'awaiting_engineering_assignment' && isDirectorUser && (
        <CollapsibleCard
          id="assign_engineer"
          className="border-primary/40 bg-primary/5"
          headerClassName="pb-2"
          title={t('assign_engineer_title', lang)}
          contentClassName="max-w-md space-y-2"
        >
            <p className="text-sm text-muted-foreground">{t('assign_engineer_hint', lang)}</p>
            <div className="flex gap-2">
              <div className="flex-1">
                <Select value={assignEngineerId} onValueChange={setAssignEngineerId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('select_placeholder', lang)} />
                  </SelectTrigger>
                  <SelectContent>
                    {users
                      .filter((u) => u.active)
                      .map((u) => (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {u.full_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleAssignEngineer}
                disabled={!assignEngineerId || metaSaving}
              >
                {t('assign_engineer', lang)}
              </Button>
            </div>
        </CollapsibleCard>
      )}

      {/* Commercial outcome (after the offer has been sent).
          Stays prominently at the top of the page so Commercial can quickly
          record the client's answer — this is what feeds the dashboard KPIs.

          Item 11: 3 estados possíveis — Ganho, Perdido, Em negociação.
          O 3º (Em negociação) marca a cotação para reabertura controlada. */}
      {rfq.status === 'offer_sent' && isCommercialUser && (
        <CollapsibleCard
          id="commercial_outcome"
          className="border-primary/40 bg-primary/5"
          title={(
            <>
              <Mail className="h-4 w-4 text-primary" /> {t('commercial_outcome_title', lang)}
            </>
          )}
          contentClassName="space-y-3"
        >
            <p className="text-sm text-muted-foreground">{t('commercial_outcome_desc', lang)}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={() => openOutcomeDialog('won')}
              >
                <Trophy className="h-4 w-4" /> {t('mark_won', lang)}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => openOutcomeDialog('lost')}
              >
                <CircleSlash className="h-4 w-4" /> {t('mark_lost', lang)}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openOutcomeDialog('in_negotiation')}
              >
                <Undo2 className="h-4 w-4" /> {t('mark_in_negotiation', lang)}
              </Button>
            </div>
        </CollapsibleCard>
      )}

      {/* Item 11: card de re-cotação quando o RFQ está em negociação.
          O Comercial reabre a cotação e abre nova versão; o fluxo passa
          obrigatoriamente por Engenharia antes de poder ser fechado como
          Won/Lost. */}
      {rfq.status === 'in_negotiation' && isCommercialUser && (
        <CollapsibleCard
          id="in_negotiation"
          className="border-amber-400/60 bg-amber-50 dark:bg-amber-950/40"
          title={(
            <>
              <Undo2 className="h-4 w-4 text-amber-600" /> {t('in_negotiation_title', lang)}
            </>
          )}
          contentClassName="space-y-3"
        >
            <p className="text-sm text-muted-foreground">{t('in_negotiation_desc', lang)}</p>
            <Button
              size="sm"
              onClick={() =>
                openDialog({
                  action: 'reopen_from_negotiation',
                  title: t('reopen_negotiation_title', lang),
                  desc: t('reopen_negotiation_desc', lang),
                  minChars: 0,
                })
              }
            >
              <Pencil className="h-4 w-4" /> {t('reopen_negotiation_action', lang)}
            </Button>
        </CollapsibleCard>
      )}

      {/* Offer PDF — generation + version listing.
          Visible whenever the RFQ has reached approval (or beyond) so the
          Commercial can produce/re-issue the commercial offer document. */}
      {['approved', 'offer_sent', 'in_negotiation', 'won', 'lost'].includes(rfq.status) && (
        <CollapsibleCard
          id="offer_pdf"
          title={(
            <>
              <FileText className="h-4 w-4" /> {t('offer_pdf_section', lang)}
            </>
          )}
          headerExtra={isCommercialUser ? (
            <Button onClick={openOfferDialog} size="sm">
              <FileText className="h-4 w-4" /> {t('generate_offer_pdf', lang)}
            </Button>
          ) : null}
        >
            {offers.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('no_offers_yet', lang)}</p>
            ) : (
              <ul className="divide-y divide-border">
                {offers.map((o) => (
                  <li
                    key={o.id}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant="outline">v{o.version}</Badge>
                      <Badge variant="secondary">
                        {(o.edited_fields?.language || 'en').toUpperCase()}
                      </Badge>
                      <span className="text-muted-foreground">
                        {formatDate(o.generated_at)}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadOffer(o)}
                    >
                      <Download className="h-4 w-4" /> {t('download', lang)}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
        </CollapsibleCard>
      )}

      {/* Contextual workflow panel by state */}
      {rfq.status === 'in_preparation' && isCommercialUser && (
        <div className="grid items-start gap-4 md:grid-cols-2">
          <CollapsibleCard
            id="gate_g1"
            className="border-warning/40"
            title={(
              <>
                <ShieldAlert className="h-4 w-4 text-warning" /> {t('gate_g1', lang)}
              </>
            )}
            contentClassName="space-y-3 text-sm"
          >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t('strategic_type', lang)}
                  </div>
                  <div className="font-medium">
                    {rfq.client?.strategic_type ? t(rfq.client.strategic_type, lang) : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t('growth_potential', lang)}
                  </div>
                  <div className="font-medium">
                    {rfq.client?.growth_potential ? t(rfq.client.growth_potential, lang) : '—'}
                  </div>
                </div>
              </div>
              {rfq.strategic_notes && (
                <div className="rounded-md bg-muted/50 px-3 py-2 text-xs">
                  {rfq.strategic_notes}
                </div>
              )}
              <EssentialDocumentsSection
                rfqId={id}
                documents={documents}
                canEdit={canAttachDocuments}
                onChanged={reloadDocuments}
                lang={lang}
                alloys={alloys}
                rfq={rfq}
                quotation={quotation}
                onApplySuggestions={applySuggestions}
                onMarkSuggested={markSuggestedFields}
              />
              <Button
                onClick={() =>
                  openDialog({
                    action: 'strategic_reject',
                    title: t('strategic_reject_title', lang),
                    desc: t('strategic_reject_desc', lang),
                    minChars: 10,
                  })
                }
                variant="destructive"
                size="sm"
                className="w-full"
              >
                <XIcon className="h-4 w-4" /> {t('strategic_reject', lang)}
              </Button>
          </CollapsibleCard>

          <CollapsibleCard
            id="gate_g2"
            className="border-info/40"
            title={(
              <>
                <FileText className="h-4 w-4 text-info" /> {t('gate_g2', lang)}
              </>
            )}
            contentClassName="space-y-3 text-sm"
          >
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('documents', lang)}</span>
                <span className="font-semibold tnum">{documents.length}</span>
              </div>
              {awaitingClient && (
                <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs">
                  <div className="mb-1 font-medium">{t('awaiting_client_pending', lang)}</div>
                  <ul className="ml-4 list-disc space-y-0.5 text-muted-foreground">
                    {pendingClientNotes.slice(0, 3).map((n) => (
                      <li key={n.id} className="truncate">{n.text}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() =>
                    openDialog({
                      action: 'request_client_info',
                      title: t('request_client_info_title', lang),
                      desc: t('request_client_info_desc', lang),
                      minChars: 1,
                    })
                  }
                  variant="outline"
                  size="sm"
                >
                  <MailQuestion className="h-4 w-4" /> {t('request_client_info', lang)}
                </Button>
                {awaitingClient && (
                  <Button onClick={handleMarkClientInfoReceived} variant="outline" size="sm">
                    <Check className="h-4 w-4" /> {t('client_info_received', lang)}
                  </Button>
                )}
              </div>

              <Separator />

              <div className="sub-surface space-y-3">
                <div className="sub-section-label">
                  {t('g2_project_info', lang)}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <RequiredLabel htmlFor="g2-project-name">{t('project_name', lang)}</RequiredLabel>
                    <Input
                      id="g2-project-name"
                      value={rfq.project_name || ''}
                      disabled={metaSaving}
                      onChange={(e) => setRfq((prev) => {
                        const next = { ...prev, project_name: e.target.value };
                        rfqRef.current = next;
                        return next;
                      })}
                      onBlur={(e) => {
                        if (g2BlurGoesToSubmit(e.relatedTarget)) return;
                        const v = e.target.value;
                        if (v !== g2Text(rfqPersistedRef.current?.project_name)) {
                          saveRfqMeta({ project_name: v });
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <RequiredLabel htmlFor="g2-plan-number">{t('plan_number', lang)}</RequiredLabel>
                    <Input
                      id="g2-plan-number"
                      value={rfq.plan_number || ''}
                      disabled={metaSaving}
                      onChange={(e) => setRfq((prev) => {
                        const next = { ...prev, plan_number: e.target.value };
                        rfqRef.current = next;
                        return next;
                      })}
                      onBlur={(e) => {
                        if (g2BlurGoesToSubmit(e.relatedTarget)) return;
                        const v = e.target.value;
                        if (v !== g2Text(rfqPersistedRef.current?.plan_number)) {
                          saveRfqMeta({ plan_number: v });
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <RequiredLabel htmlFor="g2-plan-revision">{t('plan_revision', lang)}</RequiredLabel>
                    <Input
                      id="g2-plan-revision"
                      value={rfq.plan_revision || ''}
                      disabled={metaSaving}
                      onChange={(e) => setRfq((prev) => {
                        const next = { ...prev, plan_revision: e.target.value };
                        rfqRef.current = next;
                        return next;
                      })}
                      onBlur={(e) => {
                        if (g2BlurGoesToSubmit(e.relatedTarget)) return;
                        const v = e.target.value;
                        if (v !== g2Text(rfqPersistedRef.current?.plan_revision)) {
                          saveRfqMeta({ plan_revision: v });
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <RequiredLabel htmlFor="g2-plan-revision-date">{t('plan_revision_date', lang)}</RequiredLabel>
                    <Input
                      id="g2-plan-revision-date"
                      type="date"
                      value={rfq.plan_revision_date ? rfq.plan_revision_date.substring(0, 10) : ''}
                      disabled={metaSaving}
                      onChange={(e) =>
                        setRfq((prev) => {
                          const next = {
                            ...prev,
                            plan_revision_date: g2DateInputToIso(e.target.value),
                          };
                          rfqRef.current = next;
                          return next;
                        })
                      }
                      onBlur={(e) => {
                        if (g2BlurGoesToSubmit(e.relatedTarget)) return;
                        const iso = g2DateInputToIso(e.target.value);
                        if (g2Day(iso) !== g2Day(rfqPersistedRef.current?.plan_revision_date)) {
                          saveRfqMeta({ plan_revision_date: iso });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="sub-surface space-y-3">
                <div className="sub-section-label">
                  {t('submission_data', lang)}
                </div>
                <div className="grid gap-3 sm:max-w-md">
                  <div className="space-y-1.5">
                    <RequiredLabel htmlFor="g2-deadline">{t('deadline', lang)}</RequiredLabel>
                    <Input
                      id="g2-deadline"
                      type="date"
                      value={rfq.deadline ? rfq.deadline.substring(0, 10) : ''}
                      disabled={metaSaving}
                      onChange={(e) =>
                        setRfq((prev) => {
                          const next = {
                            ...prev,
                            deadline: g2DateInputToIso(e.target.value),
                          };
                          rfqRef.current = next;
                          return next;
                        })
                      }
                      onBlur={(e) => {
                        if (g2BlurGoesToSubmit(e.relatedTarget)) return;
                        const iso = g2DateInputToIso(e.target.value);
                        if (g2Day(iso) !== g2Day(rfqPersistedRef.current?.deadline)) {
                          saveRfqMeta({ deadline: iso });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {submitReasons.length > 0 && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs">
                  <div className="mb-1 font-medium text-destructive">
                    {t('cannot_submit_reasons', lang)}
                  </div>
                  <ul className="ml-4 list-disc space-y-0.5 text-muted-foreground">
                    {submitReasons.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Button
                id="g2-submit-engineering"
                type="button"
                // Evita blur→autosave no mousedown: o botão disabled+pointer-events-none
                // engolia o click quando o utilizador submetia logo a seguir ao prazo.
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSubmitEngineering}
                size="sm"
                className="w-full"
                disabled={submitReasons.length > 0}
              >
                <Send className="h-4 w-4" /> {t('submit_engineering', lang)}
              </Button>
          </CollapsibleCard>
        </div>
      )}

      {rfq.status === 'awaiting_commercial_margins' && isCommercialUser && (() => {
        // Item 10: previsão local da decisão automática para o Comercial
        // saber, antes de submeter, se vai a APPROVED ou se será escalado
        // para a Administração. Espelha exactamente a regra do backend
        // (``submit_final_decision``) com base no PnL audit snapshot.
        const audit = quotation?.pnl_audit_snapshot || {};
        const dme = audit.delta_vs_min_ebitda_pct;
        const dvs = audit.delta_vs_schreder_pct;
        const auditOk = audit.verdict && audit.verdict !== 'ERROR' && typeof dme === 'number';
        const willEscalate = !auditOk || (typeof dme === 'number' && dme < 0);
        const willWarnAdmin = !willEscalate && typeof dvs === 'number' && dvs < 0;
        return (
          <CollapsibleCard
            id="gate_commercial_margins"
            className="border-primary/40"
            title={t('gate_commercial_margins', lang)}
            contentClassName="space-y-3 text-sm"
          >
              <p className="text-muted-foreground">{t('gate_commercial_margins_desc_v2', lang)}</p>
              <div className={`rounded-md border p-3 text-xs ${willEscalate ? 'border-amber-400/60 bg-amber-50 dark:bg-amber-950/40' : willWarnAdmin ? 'border-blue-400/60 bg-blue-50 dark:bg-blue-950/40' : 'border-emerald-400/60 bg-emerald-50 dark:bg-emerald-950/40'}`}>
                {willEscalate
                  ? t('final_decision_will_escalate', lang)
                  : willWarnAdmin
                    ? t('final_decision_will_warn_admin', lang)
                    : t('final_decision_will_approve', lang)}
              </div>
              <Button
                size="sm"
                onClick={() =>
                  openDialog({
                    action: 'submit_final_decision',
                    title: willEscalate ? t('submit_escalate_title', lang) : t('submit_approve_title', lang),
                    desc: willEscalate ? t('submit_escalate_desc', lang) : t('submit_approve_desc', lang),
                    minChars: 0,
                  })
                }
              >
                <Send className="h-4 w-4" /> {willEscalate ? t('submit_to_administration', lang) : t('submit_for_approval', lang)}
              </Button>
          </CollapsibleCard>
        );
      })()}

      {rfq.status === 'in_analysis' && isEngineerUser && (
        <CollapsibleCard
          id="gate_g3"
          className="border-info/40"
          title={(
            <>
              <HelpCircle className="h-4 w-4 text-info" /> {t('gate_g3', lang)}
              {rfq.edit_mode ? (
                <Badge variant="warning" className="ml-2 gap-1">
                  <LockOpen className="h-3 w-3" />
                  {t('version_number_short', lang).replace('{n}', String((rfq.current_form_version || 0) + 1))}
                </Badge>
              ) : rfq.current_form_version ? (
                <Badge variant="secondary" className="ml-2 gap-1">
                  <Lock className="h-3 w-3" />
                  {t('version_number_short', lang).replace('{n}', String(rfq.current_form_version))}
                </Badge>
              ) : null}
            </>
          )}
          contentClassName="space-y-3"
        >
            {rfq.edit_mode && (
              <div className="rounded-md border border-amber-400/60 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                <div className="flex items-start gap-2">
                  <Pencil className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1">
                    <div>{t('editing_mode_banner', lang)}</div>
                    {changedFields.size > 0 && (
                      <div className="font-medium">
                        {t('version_changes_count', lang).replace('{n}', String(changedFields.size))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {!rfq.edit_mode && rfq.current_form_version > 0 && isEngineerResponsible && (
              <div className="rounded-md bg-muted/50 p-3 text-xs">
                {t('locked_for_edit_info', lang)}
              </div>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              {canUnlockEng && (
                <Button onClick={handleUnlockForEdit} variant="secondary" size="sm">
                  <Pencil className="h-4 w-4" /> {t('edit_rfq_version', lang)}
                </Button>
              )}
              {canLockEng && (
                <Button
                  onClick={() => { setLockComment(''); setLockDialogOpen(true); }}
                  variant="success"
                  size="sm"
                >
                  <Lock className="h-4 w-4" /> {t('lock_edit_version', lang)}
                </Button>
              )}
              <Button
                onClick={() =>
                  openDialog({
                    action: 'return_to_commercial',
                    title: t('return_to_commercial_title', lang),
                    desc: t('return_to_commercial_desc', lang),
                    minChars: 10,
                  })
                }
                variant="outline"
                size="sm"
              >
                <Undo2 className="h-4 w-4" /> {t('return_to_commercial', lang)}
              </Button>
              {/* Item 10: novo fluxo simplificado — Engenharia → Comercial
                  (margens) directamente, sem passar pela Logística. */}
              <Button
                onClick={() =>
                  openDialog({
                    action: 'submit_to_commercial_margins',
                    title: t('submit_to_commercial_margins_title', lang),
                    desc: t('submit_to_commercial_margins_desc', lang),
                    needsRisk: true,
                    minChars: 0,
                  })
                }
                size="sm"
                className="sm:ml-auto"
                disabled={!canSubmitToCommercialMargins({ rfq, isEngineerUser })}
              >
                <Send className="h-4 w-4" /> {t('submit_to_commercial_margins', lang)}
              </Button>
            </div>
        </CollapsibleCard>
      )}

      {rfq.status === 'awaiting_direction' && isAdministrationUser && (
        <CollapsibleCard
          id="gate_g5"
          className="border-destructive/40"
          title={(
            <>
              <Gavel className="h-4 w-4 text-destructive" /> {t('gate_g5', lang)}
            </>
          )}
          contentClassName="flex flex-col gap-3 sm:flex-row"
        >
            <Button
              onClick={() =>
                openDialog({
                  action: 'approve_exception',
                  title: t('approve_exception_title', lang),
                  desc: t('approve_exception_desc', lang),
                  minChars: 10,
                })
              }
              variant="success"
              size="sm"
            >
              <Check className="h-4 w-4" /> {t('approve_exception', lang)}
            </Button>
            <Button
              onClick={() =>
                openDialog({
                  action: 'reject_final',
                  title: t('reject_final_title', lang),
                  desc: t('reject_final_desc', lang),
                  minChars: 20,
                })
              }
              variant="destructive"
              size="sm"
            >
              <XIcon className="h-4 w-4" /> {t('reject_final', lang)}
            </Button>
        </CollapsibleCard>
      )}

      {/* Versions card — shown as soon as there is at least one sealed version */}
      {versions.length > 0 && (
        <CollapsibleCard
          id="versions"
          title={(
            <>
              <HistoryIcon className="h-4 w-4" /> {t('versions_title', lang)}
            </>
          )}
          contentClassName="p-0"
        >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">{t('version_number_short', lang).replace('{n}', '')}</th>
                    <th className="px-4 py-2">{t('version_author', lang)}</th>
                    <th className="px-4 py-2">{t('version_created_at', lang)}</th>
                    <th className="px-4 py-2">{t('version_status_at_snapshot', lang)}</th>
                    <th className="px-4 py-2">{t('changes_summary_header', lang)}</th>
                    <th className="px-4 py-2 text-right">{t('view_version', lang)}</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => {
                    const labelKey = {
                      baseline: 'version_label_baseline',
                      engineering: 'version_label_engineering',
                      commercial_rework: 'version_label_commercial_rework',
                    }[v.label] || null;
                    return (
                      <tr key={v.id} className="border-t border-border/60">
                        <td className="px-4 py-2 align-top">
                          <div className="font-semibold">
                            {t('version_number_short', lang).replace('{n}', String(v.version_number))}
                          </div>
                          {labelKey && (
                            <div className="text-xs text-muted-foreground">
                              {t(labelKey, lang)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 align-top">
                          {v.author?.full_name ||
                            userDisplayMap[v.created_by]?.full_name ||
                            userDisplayMap[v.created_by]?.username ||
                            `#${v.created_by}`}
                        </td>
                        <td className="px-4 py-2 align-top tnum text-xs">
                          {formatDate(v.created_at, lang)}
                        </td>
                        <td className="px-4 py-2 align-top">
                          <Badge variant={statusBadgeVariant(v.status_at_snapshot)}>
                            {t(v.status_at_snapshot, lang)}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 align-top text-xs">
                          {v.changes_count > 0
                            ? t('version_changes_count', lang).replace('{n}', String(v.changes_count))
                            : <span className="text-muted-foreground">{t('version_no_changes', lang)}</span>}
                        </td>
                        <td className="px-4 py-2 align-top text-right">
                          <Button size="sm" variant="ghost" onClick={() => handleOpenVersion(v.id)} disabled={loadingVersion}>
                            <Eye className="h-4 w-4" /> {t('view_version', lang)}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        </CollapsibleCard>
      )}

      {/* Commercial outcome confirmation dialog (won / lost / in_negotiation) */}
      <Dialog open={!!outcomeDialog} onOpenChange={(v) => !v && !outcomeBusy && setOutcomeDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {outcomeDialog === 'won'
                ? t('mark_won_title', lang)
                : outcomeDialog === 'lost'
                  ? t('mark_lost_title', lang)
                  : t('mark_in_negotiation_title', lang)}
            </DialogTitle>
            <DialogDescription>
              {outcomeDialog === 'won'
                ? t('mark_won_desc', lang)
                : outcomeDialog === 'lost'
                  ? t('mark_lost_desc', lang)
                  : t('mark_in_negotiation_desc', lang)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {outcomeDialog === 'lost' && (
              <div className="space-y-1.5">
                <Label htmlFor="loss-reason">{t('loss_reason_label', lang)}</Label>
                <Select value={outcomeLossReason} onValueChange={setOutcomeLossReason}>
                  <SelectTrigger id="loss-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price">{t('loss_reason_price', lang)}</SelectItem>
                    <SelectItem value="lead_time">{t('loss_reason_lead_time', lang)}</SelectItem>
                    <SelectItem value="technical">{t('loss_reason_technical', lang)}</SelectItem>
                    <SelectItem value="strategic">{t('loss_reason_strategic_pass', lang)}</SelectItem>
                    <SelectItem value="competitor">{t('loss_reason_competitor', lang)}</SelectItem>
                    <SelectItem value="other">{t('loss_reason_other', lang)}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="outcome-notes">{t('outcome_notes', lang)}</Label>
              <Textarea
                id="outcome-notes"
                rows={3}
                value={outcomeNotes}
                onChange={(e) => setOutcomeNotes(e.target.value)}
                placeholder={t('comment_placeholder', lang)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOutcomeDialog(null)}
              disabled={outcomeBusy}
            >
              {t('cancel', lang)}
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmOutcome}
              disabled={outcomeBusy}
              className={outcomeDialog === 'won' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : undefined}
              variant={outcomeDialog === 'lost' ? 'destructive' : undefined}
            >
              {t('confirm_outcome', lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lock-edit comment dialog */}
      <Dialog open={lockDialogOpen} onOpenChange={(v) => !v && setLockDialogOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('lock_edit_version', lang)}</DialogTitle>
            <DialogDescription>
              {changedFields.size > 0
                ? t('version_changes_count', lang).replace('{n}', String(changedFields.size))
                : t('version_no_changes', lang)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="lock-comment">{t('lock_edit_comment_label', lang)}</Label>
            <Textarea
              id="lock-comment"
              rows={3}
              value={lockComment}
              onChange={(e) => setLockComment(e.target.value)}
              placeholder={t('comment_placeholder', lang)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setLockDialogOpen(false)} disabled={lockBusy}>
              {t('cancel', lang)}
            </Button>
            <Button size="sm" onClick={handleLockEdit} disabled={lockBusy}>
              {t('confirm', lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version detail / comparison dialog.
          ``flex flex-col max-h-[90vh]`` no DialogContent + ``flex-1 min-h-0``
          no scroll wrapper são essenciais — sem isto o conteúdo cresce além
          do viewport e a tabela de diferenças (sempre renderizada por baixo
          do snapshot) ficava recortada na borda inferior do diálogo. */}
      <Dialog open={!!versionDetail} onOpenChange={(v) => !v && setVersionDetail(null)}>
        <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-6xl flex-col sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>
              {versionDetail
                ? `${t('version_number_short', lang).replace('{n}', String(versionDetail.version_number))} — ${
                    ({
                      baseline: t('version_label_baseline', lang),
                      engineering: t('version_label_engineering', lang),
                      commercial_rework: t('version_label_commercial_rework', lang),
                    }[versionDetail.label] || '')
                  }`
                : ''}
            </DialogTitle>
            <DialogDescription>
              {versionDetail && (
                <>
                  {versionDetail.author?.full_name ||
                    userDisplayMap[versionDetail.created_by]?.full_name ||
                    userDisplayMap[versionDetail.created_by]?.username ||
                    `#${versionDetail.created_by}`}{' '}
                  •{' '}
                  {formatDate(versionDetail.created_at, lang)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {versionDetail && (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              <VersionSnapshotView
                snapshot={versionDetail.rfq_snapshot}
                changes={versionDetail.changes_summary}
                lang={lang}
                context={{
                  rfq,
                  // Maps built from already-fetched lists. Unknown ids just fall
                  // back to "#<id>" via the formatter — no extra fetch needed.
                  machinesById: Object.fromEntries((machines || []).map((m) => [m.id, m])),
                  boxesById: Object.fromEntries((boxTypes || []).map((b) => [b.id, b])),
                  usersById: userDisplayMap,
                  clientsById: rfq?.client ? { [rfq.client.id]: rfq.client.name } : {},
                }}
              />

              {versionDetail.changes_summary?.length > 0 && (
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('compare_vs_previous', lang)}
                  </div>
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40 text-muted-foreground">
                        <tr>
                          <th className="w-1/3 px-3 py-2 text-left font-medium">{t('field', lang)}</th>
                          <th className="w-1/3 px-3 py-2 text-left font-medium">{t('old_value', lang)}</th>
                          <th className="w-1/3 px-3 py-2 text-left font-medium">{t('new_value', lang)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {versionDetail.changes_summary.map((c, i) => {
                          // Caso especial: alteração de uma colecção inteira
                          // (componentes, ST, tooling, estudos). Mostramos
                          // só "Lista actualizada — N → M linhas" para não
                          // poluir a tabela com JSONs gigantes.
                          if (c.field === '__collection__') {
                            const titleKey = COLLECTION_TITLE_KEYS[c.section];
                            const label = titleKey ? t(titleKey, lang) : c.section;
                            const oldLen = Array.isArray(c.old) ? c.old.length : 0;
                            const newLen = Array.isArray(c.new) ? c.new.length : 0;
                            return (
                              <tr key={i} className="border-t border-border/60">
                                <td className="px-3 py-2 align-top">{label}</td>
                                <td className="px-3 py-2 align-top text-muted-foreground" colSpan={2}>
                                  {t('collection_changed_summary', lang)
                                    .replace('{old}', String(oldLen))
                                    .replace('{new}', String(newLen))}
                                </td>
                              </tr>
                            );
                          }
                          // Campo escalar normal — usa o mapa de labels e o
                          // formatter para mostrar valores legíveis (€/%/refs).
                          const tkey = SNAPSHOT_LABEL_KEYS[c.section]?.[c.field];
                          const label = tkey ? t(tkey, lang) : c.field;
                          const ctx = {
                            rfq,
                            machinesById: Object.fromEntries((machines || []).map((m) => [m.id, m])),
                            boxesById: Object.fromEntries((boxTypes || []).map((b) => [b.id, b])),
                            usersById: userDisplayMap,
                            clientsById: rfq?.client ? { [rfq.client.id]: rfq.client.name } : {},
                          };
                          return (
                            <tr key={i} className="border-t border-border/60">
                              <td className="px-3 py-2 align-top">{label}</td>
                              <td className="px-3 py-2 align-top tnum text-muted-foreground">
                                {formatSnapshotValue(c.field, c.old, lang, ctx)}
                              </td>
                              <td className="px-3 py-2 align-top tnum font-medium">
                                {formatSnapshotValue(c.field, c.new, lang, ctx)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!versionDetail.changes_summary?.length && (
                <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                  {t('version_no_changes', lang)}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button size="sm" onClick={() => setVersionDetail(null)}>
              {t('cancel', lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer PDF generation dialog — alargado (max-w-2xl) para acomodar
          o grid de Planning (4 inputs em 2 colunas) com labels compridos
          tipo "Primeira série após validação (semanas)" sem wrapping. */}
      <Dialog open={offerDialogOpen} onOpenChange={(v) => !offerBusy && setOfferDialogOpen(v)}>
        <DialogContent className="w-[95vw] max-w-2xl sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('generate_offer_pdf_title', lang)}</DialogTitle>
            <DialogDescription>{t('generate_offer_pdf_desc', lang)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="offer-language">{t('offer_pdf_language', lang)}</Label>
              <Select
                value={offerForm.language || 'en'}
                onValueChange={(v) => setOfferForm((p) => ({ ...p, language: v }))}
              >
                <SelectTrigger id="offer-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">{t('offer_lang_en', lang)}</SelectItem>
                  <SelectItem value="fr">{t('offer_lang_fr', lang)}</SelectItem>
                  <SelectItem value="pt">{t('offer_lang_pt', lang)}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t('offer_pdf_language_help', lang)}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-validity">{t('offer_validity_days', lang)}</Label>
              <Input
                id="offer-validity"
                type="number"
                min="1"
                placeholder="30"
                value={offerForm.validity_days}
                onChange={(e) => setOfferForm((p) => ({ ...p, validity_days: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-conditions">{t('offer_specific_conditions', lang)}</Label>
              <Textarea
                id="offer-conditions"
                rows={3}
                value={offerForm.specific_conditions}
                onChange={(e) => setOfferForm((p) => ({ ...p, specific_conditions: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-observations">{t('offer_observations', lang)}</Label>
              <Textarea
                id="offer-observations"
                rows={3}
                value={offerForm.observations}
                onChange={(e) => setOfferForm((p) => ({ ...p, observations: e.target.value }))}
              />
            </div>

            {/* Payment terms — vão para a secção PAYMENT TERMS do PDF.
                Em branco, herdam os do cliente e depois os globais, pelo que
                não são pré-preenchidos (preenchê-los fixaria os valores
                nesta oferta, quebrando essa herança). */}
            <div className="sub-surface space-y-2">
              <div className="sub-section-label">
                {t('offer_payment_terms_section', lang)}
              </div>
              <p className="text-xs text-muted-foreground">{t('offer_payment_terms_help', lang)}</p>
              <div className="space-y-1.5">
                <Label htmlFor="offer-pt-parts">{t('payment_terms_parts', lang)}</Label>
                <Textarea
                  id="offer-pt-parts"
                  rows={2}
                  value={offerForm.payment_terms_parts}
                  onChange={(e) =>
                    setOfferForm((p) => ({ ...p, payment_terms_parts: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="offer-pt-tooling">{t('payment_terms_tooling', lang)}</Label>
                <Textarea
                  id="offer-pt-tooling"
                  rows={2}
                  value={offerForm.payment_terms_tooling}
                  onChange={(e) =>
                    setOfferForm((p) => ({ ...p, payment_terms_tooling: e.target.value }))
                  }
                />
              </div>
            </div>

            {/* Planning weeks — vão para a secção PLANNING do PDF.
                Defaults 12/2/8/6 espelham a configuração global. */}
            <div className="sub-surface space-y-2">
              <div className="sub-section-label">
                {t('offer_planning_section', lang)}
              </div>
              <p className="text-xs text-muted-foreground">{t('offer_planning_help', lang)}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="offer-plan-first-article">
                    {t('offer_planning_first_article_weeks', lang)}
                  </Label>
                  <Input
                    id="offer-plan-first-article"
                    type="number"
                    min="0"
                    step="1"
                    value={offerForm.planning_first_article_weeks}
                    onChange={(e) =>
                      setOfferForm((p) => ({ ...p, planning_first_article_weeks: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="offer-plan-first-series">
                    {t('offer_planning_first_series_weeks', lang)}
                  </Label>
                  <Input
                    id="offer-plan-first-series"
                    type="number"
                    min="0"
                    step="1"
                    value={offerForm.planning_first_series_weeks}
                    onChange={(e) =>
                      setOfferForm((p) => ({ ...p, planning_first_series_weeks: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="offer-plan-supplying">
                    {t('offer_planning_supplying_weeks', lang)}
                  </Label>
                  <Input
                    id="offer-plan-supplying"
                    type="number"
                    min="0"
                    step="1"
                    value={offerForm.planning_supplying_weeks}
                    onChange={(e) =>
                      setOfferForm((p) => ({ ...p, planning_supplying_weeks: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="offer-plan-forecast">
                    {t('offer_planning_supplying_forecast_weeks', lang)}
                  </Label>
                  <Input
                    id="offer-plan-forecast"
                    type="number"
                    min="0"
                    step="1"
                    value={offerForm.planning_supplying_forecast_weeks}
                    onChange={(e) =>
                      setOfferForm((p) => ({
                        ...p,
                        planning_supplying_forecast_weeks: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOfferDialogOpen(false)} disabled={offerBusy}>
              {t('cancel', lang)}
            </Button>
            <Button size="sm" onClick={handleGenerateOffer} disabled={offerBusy}>
              {offerBusy ? t('saving_wait', lang) : t('generate_offer_pdf', lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow dialog (shared across actions) */}
      <Dialog open={!!workflowDialog} onOpenChange={(v) => !v && setWorkflowDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{workflowDialog?.title}</DialogTitle>
            {workflowDialog?.desc && (
              <DialogDescription>{workflowDialog.desc}</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-3">
            {workflowDialog?.needsRisk && (
              <div className="space-y-1.5">
                <RequiredLabel>{t('technical_risk', lang)}</RequiredLabel>
                <Select value={dialogRisk} onValueChange={setDialogRisk}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t('risk_low', lang)}</SelectItem>
                    <SelectItem value="medium">{t('risk_medium', lang)}</SelectItem>
                    <SelectItem value="high">{t('risk_high', lang)}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              {workflowDialog?.minChars > 0 ? (
                <RequiredLabel htmlFor="wf-comment">{t('comment', lang)}</RequiredLabel>
              ) : (
                <Label htmlFor="wf-comment">{t('comment', lang)}</Label>
              )}
              <Textarea
                id="wf-comment"
                rows={4}
                placeholder={t('comment_placeholder', lang)}
                value={dialogComment}
                onChange={(e) => setDialogComment(e.target.value)}
              />
              {workflowDialog?.minChars > 0 && (() => {
                const len = dialogComment.trim().length;
                const min = workflowDialog.minChars;
                const ok = len >= min;
                return (
                  <p className={`text-xs tnum ${ok ? 'text-muted-foreground' : 'text-destructive'}`}>
                    {len}/{min} — {t('min_chars_hint', lang).replace('{n}', String(min))}
                  </p>
                );
              })()}
            </div>
            {workflowDialog?.action === 'request_client_info' && (
              <div className="sub-surface space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="wf-send-email"
                    checked={dialogSendEmail}
                    onCheckedChange={(v) => setDialogSendEmail(!!v)}
                  />
                  <Label htmlFor="wf-send-email" className="cursor-pointer text-sm">
                    {t('send_email_to_client', lang)}
                  </Label>
                </div>
                {dialogSendEmail && (() => {
                  const c = rfq?.client || {};
                  const contacts = (c.email_contacts || []).filter((x) => (x.email || '').trim());
                  const noContacts = contacts.length === 0;
                  return (
                    <div className="space-y-1.5">
                      <Label htmlFor="wf-recipient-role" className="text-xs">
                        {t('email_recipient_role', lang)}
                      </Label>
                      {!noContacts && (
                        <Select
                          value={dialogEmailTo}
                          onValueChange={(v) => setDialogEmailTo(v)}
                        >
                          <SelectTrigger id="wf-recipient-role">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {contacts.map((ct, i) => (
                              <SelectItem key={`${ct.email}-${i}`} value={ct.email}>
                                {[ct.name, ct.function].filter(Boolean).join(' · ') || ct.email} — {ct.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Input
                        id="wf-email-to"
                        type="text"
                        placeholder="cliente@empresa.pt"
                        value={dialogEmailTo}
                        onChange={(e) => setDialogEmailTo(e.target.value)}
                      />
                      <p className="text-[11px] text-muted-foreground">
                        {t('email_to_recipients_help', lang)}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setWorkflowDialog(null)} disabled={dialogBusy}>
              {t('cancel', lang)}
            </Button>
            <Button
              size="sm"
              onClick={runDialogAction}
              disabled={
                dialogBusy ||
                (workflowDialog?.minChars > 0 && dialogComment.trim().length < workflowDialog.minChars) ||
                (workflowDialog?.needsRisk && !['low', 'medium', 'high'].includes(dialogRisk))
              }
            >
              {t('confirm', lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        {/* LEFT column: form */}
        <div className="space-y-4">
          <CollapsibleCard
            id="vm"
            title={t('vm_section', lang)}
            contentClassName="space-y-4"
          >
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <FieldLabel
                    label={t('alloy', lang)}
                    suggested={isSuggested('quotation.alloy_code')}
                    lang={lang}
                  />
                  <Select
                    value={quotation?.alloy_code || ''}
                    disabled={!canEditTechnicalQuotation}
                    onValueChange={async (v) => {
                      const alloy = alloys.find((a) => a.code === v);
                      if (!alloy) return;
                      updateField('alloy_code', alloy.code);
                      // Default = versão mais recente; a cotação fica presa a essa versão.
                      const versions = await loadAlloyVersions(alloy.id);
                      const latest = versions.length ? versions[versions.length - 1] : null;
                      if (latest) {
                        applyAlloyVersion(latest);
                      } else {
                        updateField('alloy_version_id', null);
                        updateField('base_mb_eur_per_t', alloy.base_mb_eur_per_t);
                        updateField('alloy_premium_eur_per_t', alloy.premium_eur_per_t);
                        updateField('alloy_premium_sell_eur_per_t', alloy.premium_sell_eur_per_t);
                        updateField('paf_percent', alloy.default_paf_percent);
                      }
                    }}
                  >
                    <SelectTrigger
                      className={isSuggested('quotation.alloy_code') ? SUGGESTED_RING : ''}
                    >
                      <SelectValue placeholder={t('select_alloy', lang)} />
                    </SelectTrigger>
                    <SelectContent>
                      {alloys
                        .filter((a) => a.active || a.code === quotation?.alloy_code)
                        .map((a) => (
                          <SelectItem key={a.id} value={a.code}>
                            {a.code} — {a.description}
                            {!a.active ? ` ${t('inactive_marker', lang)}` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t('alloy_version', lang)}</Label>
                  <Select
                    value={quotation?.alloy_version_id ? String(quotation.alloy_version_id) : ''}
                    disabled={!canEditTechnicalQuotation || !alloyVersions.length}
                    onValueChange={(v) => {
                      const version = alloyVersions.find((x) => String(x.id) === v);
                      if (version) applyAlloyVersion(version);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('select_alloy_version', lang)} />
                    </SelectTrigger>
                    <SelectContent>
                      {[...alloyVersions].reverse().map((x) => {
                        const d = x.effective_date ? String(x.effective_date).slice(0, 10) : '';
                        const totalBuy = (x.base_mb_eur_per_t || 0) + (x.premium_eur_per_t || 0);
                        return (
                          <SelectItem key={x.id} value={String(x.id)}>
                            v{x.version_number}{d ? ` · ${d}` : ''} · €{totalBuy.toFixed(0)}/T
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t('calc_mode', lang)}</Label>
                  <Select
                    value={quotation?.calc_mode || 'legacy_excel_mode'}
                    disabled={!canEditTechnicalQuotation}
                    onValueChange={(v) => updateField('calc_mode', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="legacy_excel_mode">{t('legacy_mode', lang)}</SelectItem>
                      <SelectItem value="enhanced_cost_mode">{t('enhanced_mode', lang)}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-4">
                <NumField id="base-mb" label={t('base_mb', lang)} value={quotation?.base_mb_eur_per_t} step={1} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.base_mb_eur_per_t')} onChange={(v) => updateField('base_mb_eur_per_t', v || 0)} />
                <ReadOnlyNumField id="premium-buy" label={t('premium_buy', lang)} value={quotation?.alloy_premium_eur_per_t} />
                <ReadOnlyNumField id="premium-sell" label={t('premium_sell', lang)} value={quotation?.alloy_premium_sell_eur_per_t} />
                <PercentField id="paf" label={t('paf', lang)} value={quotation?.paf_percent} step={0.1} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.paf_percent')} onChange={(v) => updateField('paf_percent', v || 0)} />
              </div>
              {isBaseMbOverride && (
                <div className="rounded-md border border-warning/40 bg-warning/10 p-3 space-y-2">
                  <div className="text-xs font-medium text-warning-foreground">
                    {t('base_mb_overridden', lang)}
                  </div>
                  <div className="space-y-1.5">
                    <RequiredLabel htmlFor="base-mb-comment">{t('base_mb_edit_comment', lang)}</RequiredLabel>
                    <Textarea
                      id="base-mb-comment"
                      rows={2}
                      value={quotation?.base_mb_override_comment || ''}
                      disabled={!canEditTechnicalQuotation}
                      onChange={(e) => updateField('base_mb_override_comment', e.target.value)}
                    />
                    <div className="flex items-center justify-between gap-2">
                      {baseMbCommentMissing ? (
                        <p className="text-xs text-destructive">{t('base_mb_comment_required', lang)}</p>
                      ) : baseMbCommentTooShort ? (
                        <p className="text-xs text-destructive">
                          {t('base_mb_comment_min', lang).replace('{n}', BASE_MB_COMMENT_MIN_LEN)}
                        </p>
                      ) : (
                        <span />
                      )}
                      <span className={`text-xs tnum ${baseMbCommentTooShort ? 'text-destructive' : 'text-muted-foreground'}`}>
                        {_baseMbCommentLen}/{BASE_MB_COMMENT_MIN_LEN}
                      </span>
                    </div>
                  </div>
                  {quotation?.base_mb_override_at && (
                    <div className="text-xs text-muted-foreground">
                      {t('base_mb_overridden_by', lang)}:{' '}
                      {userDisplayMap[quotation.base_mb_override_by]?.full_name
                        || userDisplayMap[quotation.base_mb_override_by]?.username
                        || (quotation.base_mb_override_by ? `#${quotation.base_mb_override_by}` : '—')}{' '}
                      · {String(quotation.base_mb_override_at).substring(0, 10)}
                    </div>
                  )}
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-2">
                <NumField id="piece-w" label={t('piece_weight', lang)} value={quotation?.piece_weight_kg} step={0.001} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.piece_weight_kg')} suggested={isSuggested('quotation.piece_weight_kg')} lang={lang} onChange={(v) => updateField('piece_weight_kg', v || 0)} />
                <NumField id="runner-w" label={t('runner_weight', lang)} value={quotation?.runner_weight_kg} step={0.001} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.runner_weight_kg')} onChange={(v) => updateField('runner_weight_kg', v || 0)} />
              </div>
          </CollapsibleCard>

          <CollapsibleCard
            id="va_foundry"
            title={t('va_foundry_section', lang)}
            contentClassName="space-y-4"
          >
              <div className="grid gap-4 md:grid-cols-3">
                <NumField
                  id="piece-l-mm"
                  label={t('piece_length_mm', lang)}
                  value={quotation?.piece_length_mm}
                  step={0.1}
                  disabled={!canEditTechnicalQuotation}
                  highlight={highlight('quotation.piece_length_mm')}
                  suggested={isSuggested('quotation.piece_length_mm')}
                  lang={lang}
                  onChange={(v) => updateField('piece_length_mm', v ?? null)}
                />
                <NumField
                  id="piece-w-mm"
                  label={t('piece_width_mm', lang)}
                  value={quotation?.piece_width_mm}
                  step={0.1}
                  disabled={!canEditTechnicalQuotation}
                  highlight={highlight('quotation.piece_width_mm')}
                  suggested={isSuggested('quotation.piece_width_mm')}
                  lang={lang}
                  onChange={(v) => updateField('piece_width_mm', v ?? null)}
                />
                <NumField
                  id="piece-h-mm"
                  label={t('piece_height_mm', lang)}
                  value={quotation?.piece_height_mm}
                  step={0.1}
                  disabled={!canEditTechnicalQuotation}
                  highlight={highlight('quotation.piece_height_mm')}
                  suggested={isSuggested('quotation.piece_height_mm')}
                  lang={lang}
                  onChange={(v) => updateField('piece_height_mm', v ?? null)}
                />
              </div>
              <NumField
                id="machine-pressure-bar"
                label={t('machine_pressure_bar', lang)}
                value={quotation?.machine_pressure_bar}
                step={0.1}
                disabled={!canEditTechnicalQuotation}
                highlight={highlight('quotation.machine_pressure_bar')}
                onChange={(v) => updateField('machine_pressure_bar', v ?? null)}
              />
              <div className="grid gap-4 md:grid-cols-3">
                {/* Coef. de projecção — fracção da bounding box L×W
                    ocupada pela peça (default 0.7; sobe para 0.9–1.0
                    em peças que ocupam quase todo o rectângulo). */}
                <div>
                  <NumField id="projection-coef" label={t('projection_coef', lang)} value={quotation?.projection_coef ?? 0.7} step={0.05} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.projection_coef')} onChange={(v) => updateField('projection_coef', v ?? 0.7)} />
                  <p className="mt-1 text-xs text-muted-foreground">{t('projection_coef_hint', lang)}</p>
                </div>
                {/* MACH-02 — área projectada derivada de length × width × projection_coef
                    (read-only para o comercial; editável só pela engenharia
                    em in_analysis com edit_mode). */}
                <NumField id="proj-area" label={t('projected_area', lang)} value={quotation?.projected_area_cm2} step={0.1} disabled={!canEditMachineAndArea} highlight={highlight('quotation.projected_area_cm2')} onChange={(v) => updateField('projected_area_cm2', v || 0)} />
                {/* MACH-02 — Tonelagem calculada (T) — sempre read-only.
                    Mostra um aviso quando a Pressão Máquina está em falta,
                    já que zera a fórmula. */}
                <div>
                  <NumField id="calc-tonnage" label={t('calculated_tonnage', lang)} value={quotation?.calculated_tonnage_t} step={0.1} disabled />
                  {!quotation?.machine_pressure_bar && (
                    <p className="mt-1 text-xs text-amber-600">
                      {t('tonnage_missing_inputs', lang)}
                    </p>
                  )}
                </div>
              </div>
              {/* MACH-02 rev Ago 2026 — coeficientes da fórmula de selecção
                  de máquina, todos introduzidos como 0–1 e aplicados como
                  (1 + coef): safety_factor = "Coef. gavetas" (0.1 sem /
                  0.2 com), mold_complexity_coef = "Coef. de segurança"
                  (0.5 default), runner_weight_coef = "Coef. peso gito". */}
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <NumField id="safety" label={t('safety_factor', lang)} value={quotation?.safety_factor} step={0.05} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.safety_factor')} onChange={(v) => updateField('safety_factor', Math.min(Math.max(Number(v) || 0, 0), 1))} />
                  <p className="mt-1 text-xs text-muted-foreground">{t('safety_factor_hint', lang)}</p>
                </div>
                <div>
                  <NumField id="mold-complexity" label={t('mold_complexity_coef', lang)} value={quotation?.mold_complexity_coef} step={0.05} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.mold_complexity_coef')} onChange={(v) => updateField('mold_complexity_coef', Math.min(Math.max(Number(v) || 0, 0), 1))} />
                  <p className="mt-1 text-xs text-muted-foreground">{t('mold_complexity_hint', lang)}</p>
                </div>
                <div>
                  <NumField
                    id="runner-weight-coef"
                    label={t('runner_weight_coef', lang)}
                    value={quotation?.runner_weight_coef ?? 0}
                    step={0.05}
                    disabled={!canEditTechnicalQuotation}
                    highlight={highlight('quotation.runner_weight_coef')}
                    onChange={(v) => updateField('runner_weight_coef', Math.min(Math.max(Number(v) || 0, 0), 1))}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">{t('runner_weight_coef_hint', lang)}</p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>
                    {t('machine', lang)}
                    {!quotation?.machine_override
                      && (quotation?.machine_id || calcResult?.machine?.selected_machine) && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        ({t('auto_selected', lang)})
                      </span>
                    )}
                  </Label>
                  {/* MACH-02 — auto-seleccionada pela fórmula; editável só
                      pela engenharia. O Select mostra a máquina concreta
                      escolhida pelo motor; voltar a 'auto' redefine o
                      override e deixa o backend resolver de novo. */}
                  <Select
                    value={quotation?.machine_override && quotation?.machine_id ? String(quotation.machine_id) : 'auto'}
                    disabled={!canEditMachineAndArea}
                    onValueChange={(v) => {
                      if (v === 'auto') {
                        updateField('machine_id', null);
                        updateField('machine_override', false);
                      } else {
                        updateField('machine_id', parseInt(v, 10));
                        updateField('machine_override', true);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">
                        {t('auto_by_area', lang)}
                        {!quotation?.machine_override && (() => {
                          // A máquina resolvida pelo motor vem no calc_snapshot
                          // (machine.selected_machine), actualizado a cada
                          // gravação — o quotation.machine_id local só é
                          // refrescado num reload completo da página. Usamos o
                          // cálculo como fonte principal para o nome aparecer
                          // logo após guardar, com fallback ao estado carregado.
                          const sel = calcResult?.machine?.selected_machine;
                          const auto = machines.find(
                            (m) => m.id === (sel?.id ?? quotation?.machine_id),
                          ) || sel;
                          return auto ? ` — ${auto.name} (${auto.tonnage}T)` : '';
                        })()}
                      </SelectItem>
                      {machines
                        .filter((m) => m.active)
                        .map((m) => (
                          <SelectItem key={m.id} value={String(m.id)}>
                            {m.name} — {m.tonnage}T (€{m.hourly_rate_eur}/h)
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <IntField id="cavities" label={t('cavities', lang)} value={quotation?.num_cavities ?? 1} min={1} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.num_cavities')} onChange={(v) => updateField('num_cavities', v || 1)} />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-1">
                  <NumField id="cycle-time" label={t('cycle_time_per_piece', lang)} value={_cycleTimeS} step={0.1} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.cycle_time_s')} onChange={(v) => updateCycleTime(v)} />
                  {cycleSuggestion && (
                    <p className="text-[11px] leading-snug text-muted-foreground">
                      {t('ct_suggestion_label', lang)}:{' '}
                      <span className="font-medium text-foreground">
                        {cycleSuggestion.suggested_cycle_time_s}s
                      </span>{' '}
                      ({cycleSuggestion.sample_count} {t('ct_suggestion_records', lang)},{' '}
                      {cycleSuggestion.machine_matched
                        ? _suggMachineName
                        : t('ct_suggestion_all_machines', lang)})
                      {canEditTechnicalQuotation && (
                        <button
                          type="button"
                          className="ml-2 font-medium text-primary underline underline-offset-2"
                          onClick={() => updateCycleTime(cycleSuggestion.suggested_cycle_time_s)}
                        >
                          {t('ct_apply', lang)}
                        </button>
                      )}
                    </p>
                  )}
                </div>
                <PercentField id="trs" label={t('trs', lang)} value={quotation?.trs_percent} step={0.1} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.trs_percent')} onChange={(v) => updateField('trs_percent', v || 0)} />
                <PercentField id="scrap" label={t('scrap_rate', lang)} value={quotation?.scrap_rate_foundry_percent} step={0.1} disabled={!canEditTechnicalQuotation} highlight={highlight('quotation.scrap_rate_foundry_percent')} onChange={(v) => updateField('scrap_rate_foundry_percent', v || 0)} />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <ReadOnlyNumField id="shots-day" label={t('shots_per_day', lang)} value={shotsPerDay} />
                <ReadOnlyNumField id="pieces-day" label={t('pieces_per_day', lang)} value={piecesPerDay} />
                <ReadOnlyNumField id="pieces-day-net" label={t('pieces_per_day_no_scrap', lang)} value={piecesPerDayNet} />
              </div>
          </CollapsibleCard>

          <CollapsibleCard
            id="va_finishing"
            title={t('va_finishing_section', lang)}
            contentClassName="space-y-2"
          >
              {/* Larguras dos campos: o histórico ficou em w-24 (96px) e cortava
                  números maiores (cadências de 4 dígitos, taxas com 2 casas
                  decimais). Aumentado para w-32 (128px) — espaço suficiente
                  para os valores típicos sem partir o flex layout. */}
              <div className="hidden gap-2 px-3 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground sm:flex">
                <div className="min-w-[180px]">{t('finishing_op_name', lang)}</div>
                <div className="w-32 text-center">{t('finishing_cadence_hdr', lang)}</div>
                <div className="w-32 text-center">{t('finishing_rate_hdr', lang)}</div>
                <div className="w-32 text-center">{t('finishing_scrap_hdr', lang)}</div>
              </div>
              {(quotation?.finishing_operations || []).map((op, i) => (
                <div
                  key={i}
                  className={`flex flex-wrap items-center gap-2 rounded-md px-3 py-2 ${
                    op.enabled ? 'bg-primary/5' : 'bg-muted/40'
                  }`}
                >
                  <label className="flex min-w-[180px] items-center gap-2 text-sm font-medium">
                    <Checkbox
                      checked={op.enabled}
                      disabled={!canEditTechnicalQuotation}
                      onCheckedChange={(v) => updateFinishingOp(i, 'enabled', Boolean(v))}
                      aria-label={finishingOpLabel(op.name, lang)}
                    />
                    {finishingOpLabel(op.name, lang)}
                  </label>
                  {op.enabled && (
                    <>
                      <Input
                        type="number"
                        className="w-32"
                        placeholder={t('finishing_cadence_placeholder', lang)}
                        aria-label={t('finishing_cadence_hdr', lang)}
                        title={t('finishing_cadence_hdr', lang)}
                        value={op.cadence_pcs_hr}
                        disabled={!canEditTechnicalQuotation}
                        onChange={(e) => updateFinishingOp(i, 'cadence_pcs_hr', parseFloat(e.target.value) || 0)}
                      />
                      <div
                        className="flex h-9 w-32 items-center justify-end rounded-md border border-input bg-muted/50 px-2 text-xs tabular-nums text-muted-foreground"
                        title={t('finishing_rate_from_config', lang)}
                      >
                        {op.rate_eur_hr != null && op.rate_eur_hr !== '' ? Number(op.rate_eur_hr).toFixed(2) : '—'}
                      </div>
                      <Input
                        type="number"
                        step={0.1}
                        className="w-32"
                        placeholder={t('finishing_scrap_placeholder', lang)}
                        aria-label={t('finishing_scrap_hdr', lang)}
                        title={t('finishing_scrap_hdr', lang)}
                        value={op.scrap_percent == null || op.scrap_percent === '' ? '' : Number((op.scrap_percent * 100).toFixed(4))}
                        disabled={!canEditTechnicalQuotation}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const num = raw === '' ? 0 : parseFloat(raw);
                          if (Number.isNaN(num)) return;
                          updateFinishingOp(i, 'scrap_percent', num / 100);
                        }}
                      />
                    </>
                  )}
                </div>
              ))}
          </CollapsibleCard>

          {/* ===================== TRABALHOS EXTRA (Maquinagem CNC, Controlo …) =====================
              Mesma UI do "VA Acabamento" mas operando sobre `extra_works_operations`. As ops
              configuradas pelo admin em FinishingTab com category='extra_works'. */}
          <CollapsibleCard
            id="extra_works"
            title={t('extra_works_section', lang)}
            contentClassName="space-y-2"
          >
              {(quotation?.extra_works_operations || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('extra_works_empty', lang)}</p>
              ) : (
                <>
                  <div className="hidden gap-2 px-3 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground sm:flex">
                    <div className="min-w-[180px]">{t('finishing_op_name', lang)}</div>
                    <div className="w-32 text-center">{t('finishing_cadence_hdr', lang)}</div>
                    <div className="w-32 text-center">{t('finishing_rate_hdr', lang)}</div>
                    <div className="w-32 text-center">{t('finishing_scrap_hdr', lang)}</div>
                  </div>
                  {(quotation?.extra_works_operations || []).map((op, i) => (
                    <div
                      key={i}
                      className={`flex flex-wrap items-center gap-2 rounded-md px-3 py-2 ${
                        op.enabled ? 'bg-primary/5' : 'bg-muted/40'
                      }`}
                    >
                      <label className="flex min-w-[180px] items-center gap-2 text-sm font-medium">
                        <Checkbox
                          checked={op.enabled}
                          disabled={!canEditTechnicalQuotation}
                          onCheckedChange={(v) => updateExtraWorksOp(i, 'enabled', Boolean(v))}
                          aria-label={finishingOpLabel(op.name, lang)}
                        />
                        {finishingOpLabel(op.name, lang)}
                      </label>
                      {op.enabled && (
                        <>
                          <Input
                            type="number"
                            className="w-32"
                            placeholder={t('finishing_cadence_placeholder', lang)}
                            aria-label={t('finishing_cadence_hdr', lang)}
                            title={t('finishing_cadence_hdr', lang)}
                            value={op.cadence_pcs_hr}
                            disabled={!canEditTechnicalQuotation}
                            onChange={(e) => updateExtraWorksOp(i, 'cadence_pcs_hr', parseFloat(e.target.value) || 0)}
                          />
                          <div
                            className="flex h-9 w-32 items-center justify-end rounded-md border border-input bg-muted/50 px-2 text-xs tabular-nums text-muted-foreground"
                            title={t('finishing_rate_from_config', lang)}
                          >
                            {op.rate_eur_hr != null && op.rate_eur_hr !== '' ? Number(op.rate_eur_hr).toFixed(2) : '—'}
                          </div>
                          <Input
                            type="number"
                            step={0.1}
                            className="w-32"
                            placeholder={t('finishing_scrap_placeholder', lang)}
                            aria-label={t('finishing_scrap_hdr', lang)}
                            title={t('finishing_scrap_hdr', lang)}
                            value={op.scrap_percent == null || op.scrap_percent === '' ? '' : Number((op.scrap_percent * 100).toFixed(4))}
                            disabled={!canEditTechnicalQuotation}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const num = raw === '' ? 0 : parseFloat(raw);
                              if (Number.isNaN(num)) return;
                              updateExtraWorksOp(i, 'scrap_percent', num / 100);
                            }}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </>
              )}
          </CollapsibleCard>

          {/* ===================== COMPONENTS (calc_v2) ===================== */}
          <CollapsibleCard
            id="components"
            title={t('components_section', lang)}
            headerExtra={canEditTechnicalQuotation ? (
              <Button type="button" size="sm" variant="outline" onClick={addComponent}>
                + {t('add_line', lang)}
              </Button>
            ) : null}
          >
              {(quotation?.components || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('no_components_yet', lang)}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-2 py-1 text-left">{t('component_reference', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('component_purchase', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('component_transport', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('component_qty', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('component_scrap', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('component_shrink', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('component_supplier_margin', lang)}</th>
                        {canEditTechnicalQuotation && <th className="px-2 py-1" />}
                      </tr>
                    </thead>
                    <tbody>
                      {(quotation?.components || []).map((c) => (
                        <tr key={c.row_uid} className="border-t border-border">
                          <td className="px-2 py-1">
                            <Input value={c.reference || ''} disabled={!canEditTechnicalQuotation}
                              onChange={(e) => updateComponent(c.row_uid, 'reference', e.target.value)} />
                          </td>
                          <td className="px-2 py-1"><Input type="number" step="0.01" disabled={!canEditTechnicalQuotation}
                            value={c.purchase_cost_eur ?? 0}
                            onChange={(e) => updateComponent(c.row_uid, 'purchase_cost_eur', parseFloat(e.target.value) || 0)} /></td>
                          <td className="px-2 py-1"><Input type="number" step="0.01" disabled={!canEditTechnicalQuotation}
                            value={c.transport_eur ?? 0}
                            onChange={(e) => updateComponent(c.row_uid, 'transport_eur', parseFloat(e.target.value) || 0)} /></td>
                          <td className="px-2 py-1"><Input type="number" step="1" disabled={!canEditTechnicalQuotation}
                            value={c.qty_per_piece ?? 1}
                            onChange={(e) => updateComponent(c.row_uid, 'qty_per_piece', parseFloat(e.target.value) || 0)} /></td>
                          <td className="px-2 py-1"><Input type="number" step="0.1" disabled={!canEditTechnicalQuotation}
                            value={((c.scrap_detected ?? 0) * 100).toFixed(2)}
                            onChange={(e) => updateComponent(c.row_uid, 'scrap_detected', (parseFloat(e.target.value) || 0) / 100)} /></td>
                          <td className="px-2 py-1"><Input type="number" step="0.1" disabled={!canEditTechnicalQuotation}
                            value={((c.inventory_shrink ?? 0) * 100).toFixed(2)}
                            onChange={(e) => updateComponent(c.row_uid, 'inventory_shrink', (parseFloat(e.target.value) || 0) / 100)} /></td>
                          <td className="px-2 py-1"><Input type="number" step="0.1" disabled={!canEditTechnicalQuotation}
                            value={((c.supplier_margin ?? 0) * 100).toFixed(2)}
                            onChange={(e) => updateComponent(c.row_uid, 'supplier_margin', (parseFloat(e.target.value) || 0) / 100)} /></td>
                          {canEditTechnicalQuotation && (
                            <td className="px-2 py-1 text-right">
                              <Button type="button" size="sm" variant="ghost" onClick={() => removeComponent(c.row_uid)}>×</Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </CollapsibleCard>

          {/* ===================== SUBCONTRACT CANDIDATES ===================== */}
          <CollapsibleCard
            id="subcontract"
            title={t('subcontract_section', lang)}
            contentClassName="space-y-4"
          >
              {['machining', 'surface_treatment', 'painting', 'other'].map((cat) => {
                const cands = (quotation?.subcontract_candidates || []).filter((c) => c.category === cat);
                return (
                  <div key={cat} className="sub-surface">
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="text-sm font-semibold">{t(`subcontract_${cat}`, lang)}</h4>
                      {canEditTechnicalQuotation && (
                        <Button type="button" size="sm" variant="outline" onClick={() => addSubcontractCandidate(cat)}>
                          + {t('add_candidate', lang)}
                        </Button>
                      )}
                    </div>
                    {cands.length === 0 ? (
                      <p className="text-xs text-muted-foreground">{t('no_candidates_yet', lang)}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="uppercase tracking-wider text-muted-foreground">
                            <tr>
                              <th className="px-2 py-1 text-center">{t('selected', lang)}</th>
                              <th className="px-2 py-1 text-left">{t('supplier', lang)}</th>
                              <th className="px-2 py-1 text-right">{t('purchase', lang)}</th>
                              <th className="px-2 py-1 text-right">{t('transport_pallet', lang)}</th>
                              <th className="px-2 py-1 text-right">{t('pcs_pallet', lang)}</th>
                              <th className="px-2 py-1 text-right">{t('scrap_percent', lang)}</th>
                              {canEditTechnicalQuotation && <th className="px-2 py-1" />}
                            </tr>
                          </thead>
                          <tbody>
                            {cands.map((c) => (
                              <tr key={c.row_uid} className="border-t border-border">
                                <td className="px-2 py-1 text-center">
                                  <input type="radio" disabled={!canEditTechnicalQuotation}
                                    checked={!!c.selected}
                                    onChange={() => selectSubcontractCandidate(c.row_uid)} />
                                </td>
                                <td className="px-2 py-1">
                                  <SupplierSelect
                                    value={c.supplier || ''}
                                    category={cat}
                                    suppliers={suppliers}
                                    lang={lang}
                                    disabled={!canEditTechnicalQuotation}
                                    onChange={(v) => updateSubcontractCandidate(c.row_uid, 'supplier', v)}
                                  />
                                </td>
                                <td className="px-2 py-1"><Input type="number" step="0.01" disabled={!canEditTechnicalQuotation}
                                  value={c.purchase_cost_eur ?? 0}
                                  onChange={(e) => updateSubcontractCandidate(c.row_uid, 'purchase_cost_eur', parseFloat(e.target.value) || 0)} /></td>
                                <td className="px-2 py-1"><Input type="number" step="0.01" disabled={!canEditTechnicalQuotation}
                                  value={c.transport_per_pallet_eur ?? 0}
                                  onChange={(e) => updateSubcontractCandidate(c.row_uid, 'transport_per_pallet_eur', parseFloat(e.target.value) || 0)} /></td>
                                <td className="px-2 py-1"><Input type="number" step="1" disabled={!canEditTechnicalQuotation}
                                  value={c.pcs_per_pallet ?? 0}
                                  onChange={(e) => updateSubcontractCandidate(c.row_uid, 'pcs_per_pallet', parseInt(e.target.value, 10) || 0)} /></td>
                                <td className="px-2 py-1"><Input type="number" step="0.1" disabled={!canEditTechnicalQuotation}
                                  value={((c.scrap_percent ?? 0) * 100).toFixed(2)}
                                  onChange={(e) => updateSubcontractCandidate(c.row_uid, 'scrap_percent', (parseFloat(e.target.value) || 0) / 100)} /></td>
                                {canEditTechnicalQuotation && (
                                  <td className="px-2 py-1 text-right">
                                    <Button type="button" size="sm" variant="ghost"
                                      onClick={() => removeSubcontractCandidate(c.row_uid)}>×</Button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
          </CollapsibleCard>

          {/* ===================== EMBALAGEM ===================== */}
          <CollapsibleCard
            id="packaging"
            title={t('packaging_section_card', lang)}
            contentClassName="grid gap-4 md:grid-cols-3"
          >
              <div className="space-y-1.5 md:col-span-3">
                <Label>{t('box_type_label', lang)}</Label>
                <Select
                  value={quotation?.box_type_id != null ? String(quotation.box_type_id) : 'none'}
                  disabled={!canEditPackagingTransport}
                  onValueChange={(v) => updateField('box_type_id', v === 'none' ? null : parseInt(v, 10))}
                >
                  <SelectTrigger className={highlight('quotation.box_type_id') ? DIFF_RING : ''}>
                    <SelectValue placeholder={t('box_type_placeholder', lang)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('box_type_none', lang)}</SelectItem>
                    {(boxTypes || []).map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.name} — {b.length_mm}×{b.width_mm}×{b.height_mm} mm — {formatEuro(b.price_eur, lang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {quotation?.box_type_id == null && (
                  <p className="text-[11px] text-muted-foreground">{t('packaging_manual_counts_hint', lang)}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <IntField
                  id="pcs-box"
                  label={t('pieces_per_box', lang)}
                  value={quotation?.pieces_per_box}
                  disabled={!canEditPackagingTransport}
                  onChange={(v) => updateField('pieces_per_box', v || 0)}
                />
                {quotation?.box_type_id != null && (
                  <p className="text-[11px] text-muted-foreground">{t('pieces_per_box_auto_hint', lang)}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <IntField
                  id="box-pal"
                  label={t('boxes_per_pallet', lang)}
                  value={quotation?.boxes_per_pallet}
                  disabled={!canEditPackagingTransport}
                  onChange={(v) => updateField('boxes_per_pallet', v || 0)}
                />
                {quotation?.box_type_id != null && (
                  <p className="text-[11px] text-muted-foreground">{t('boxes_per_pallet_auto_hint', lang)}</p>
                )}
              </div>
              <IntField
                id="separators-box"
                label={t('separators_per_box', lang)}
                value={quotation?.packaging_separators_per_box}
                disabled={!canEditPackagingTransport}
                onChange={(v) => updateField('packaging_separators_per_box', v || 0)}
              />
              <div className="space-y-1.5">
                <ReadOnlyNumField
                  id="pallet-cost"
                  label={t('pallet_cost', lang)}
                  value={
                    quotation?.pallet_cost_eur == null
                      ? '—'
                      : formatEuro(quotation.pallet_cost_eur, lang)
                  }
                />
                <p className="text-[11px] text-muted-foreground">{t('pallet_cost_readonly_hint', lang)}</p>
              </div>
              <ReadOnlyNumField
                id="max-pw"
                label={t('max_pallet_weight', lang)}
                value={1500}
              />
          </CollapsibleCard>

          {/* ===================== TRANSPORTE ===================== */}
          <CollapsibleCard
            id="transport"
            title={t('transport_section_card', lang)}
            contentClassName="grid gap-4 md:grid-cols-3"
          >
              <div className="space-y-1.5 md:col-span-3">
                <Label>{t('transport_incoterm', lang)}</Label>
                <Select
                  value={transportInc}
                  disabled={!canEditPackagingTransport}
                  onValueChange={(v) => updateField('transport_incoterm', v)}
                >
                  <SelectTrigger className={highlight('quotation.transport_incoterm') ? DIFF_RING : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ddp">DDP</SelectItem>
                    <SelectItem value="dap">DAP</SelectItem>
                    <SelectItem value="exwork">Ex Works</SelectItem>
                    <SelectItem value="other">{t('transport_incoterm_other', lang)}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {showTransportLotFields && (
                <>
                  <NumField
                    id="transp"
                    label={t('transport_per_lot', lang)}
                    value={quotation?.transport_cost_eur_per_lot}
                    step={0.01}
                    disabled={!canEditPackagingTransport}
                    highlight={highlight('quotation.transport_cost_eur_per_lot')}
                    onChange={(v) => updateField('transport_cost_eur_per_lot', v || 0)}
                  />
                  <IntField
                    id="ppl"
                    label={t('pieces_per_lot', lang)}
                    value={quotation?.transport_pieces_per_lot}
                    disabled={!canEditPackagingTransport}
                    highlight={highlight('quotation.transport_pieces_per_lot')}
                    onChange={(v) => updateField('transport_pieces_per_lot', v || 0)}
                  />
                </>
              )}
              {showTransportKmFields && (
                <>
                  <NumField
                    id="trans-km"
                    label={t('transport_km', lang)}
                    value={quotation?.transport_km}
                    step={0.1}
                    disabled={!canEditPackagingTransport}
                    highlight={highlight('quotation.transport_km')}
                    onChange={(v) => updateField('transport_km', v || 0)}
                  />
                  <NumField
                    id="trans-eur-km"
                    label={t('transport_eur_per_km', lang)}
                    value={quotation?.transport_eur_per_km}
                    step={0.01}
                    disabled={!canEditPackagingTransport}
                    highlight={highlight('quotation.transport_eur_per_km')}
                    onChange={(v) => updateField('transport_eur_per_km', v || 0)}
                  />
                  <IntField
                    id="pcs-trip"
                    label={t('transport_pieces_per_trip', lang)}
                    value={quotation?.transport_pieces_per_trip}
                    disabled={!canEditPackagingTransport}
                    highlight={highlight('quotation.transport_pieces_per_trip')}
                    onChange={(v) => updateField('transport_pieces_per_trip', v || 0)}
                  />
                </>
              )}
          </CollapsibleCard>

          {/* ===================== MOLDES / FERRAMENTAS (4 linhas pré-criadas + custom) ===================== */}
          <CollapsibleCard
            id="tooling"
            title={t('tooling_section', lang)}
          >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1 text-left">{t('tooling_line_label', lang)}</th>
                      <th className="px-2 py-1 text-right">{t('tooling_cost', lang)}</th>
                      <th className="px-2 py-1 text-right">{t('tooling_margin', lang)}</th>
                      <th className="px-2 py-1 text-right">{t('tooling_guarantee_shots', lang)}</th>
                      <th className="px-2 py-1 text-right">{t('tooling_sale', lang)}</th>
                      <th className="px-2 py-1 w-10" aria-label="actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(quotation?.tooling_lines) ? quotation.tooling_lines : []).map((l, i) => {
                      const toolingHl = highlight('tooling_lines');
                      const isCustom = isCustomToolingLine(l.label);
                      return (
                      <tr key={l._key ?? l.id ?? i} className={`border-t border-border ${toolingHl ? 'bg-amber-50/60 dark:bg-amber-950/25' : ''}`}>
                        <td className="px-2 py-1">
                          {isCustom ? (
                            <Input
                              type="text"
                              disabled={!canEditTechnicalQuotation}
                              className={toolingHl ? DIFF_RING : ''}
                              placeholder={t('tooling_custom_label_placeholder', lang)}
                              value={l.label || ''}
                              onChange={(e) => updateToolingLine(i, 'label', e.target.value)}
                            />
                          ) : (
                            t(`tooling_label_${l.label}`, lang) || l.label
                          )}
                        </td>
                        <td className="px-2 py-1"><Input type="number" step="1" disabled={!canEditTechnicalQuotation}
                          className={toolingHl ? DIFF_RING : ''}
                          value={l.cost_eur ?? 0}
                          onChange={(e) => updateToolingLine(i, 'cost_eur', parseFloat(e.target.value) || 0)} /></td>
                        <td className="px-2 py-1"><Input type="number" step="0.1" disabled={!canEditTechnicalQuotation}
                          className={toolingHl ? DIFF_RING : ''}
                          value={((l.margin_percent ?? 0) * 100).toFixed(2)}
                          onChange={(e) => updateToolingLine(i, 'margin_percent', (parseFloat(e.target.value) || 0) / 100)} /></td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            disabled={!canEditTechnicalQuotation}
                            className={toolingHl ? DIFF_RING : ''}
                            value={l.guarantee_shots ?? ''}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') {
                                updateToolingLine(i, 'guarantee_shots', null);
                              } else {
                                const parsed = parseInt(raw, 10);
                                updateToolingLine(i, 'guarantee_shots', Number.isFinite(parsed) ? parsed : null);
                              }
                            }}
                          />
                        </td>
                        <td className="px-2 py-1 text-right tnum">
                          {formatEuro((l.cost_eur || 0) * (1 + (l.margin_percent || 0)), lang)}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {isCustom && canEditTechnicalQuotation ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              aria-label={t('tooling_remove_line', lang)}
                              onClick={() => removeToolingLine(i)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
              {canEditTechnicalQuotation && (
                <div className="mt-3">
                  <Button type="button" size="sm" variant="outline" onClick={addToolingLine}>
                    <Plus className="h-4 w-4" /> {t('tooling_add_line', lang)}
                  </Button>
                </div>
              )}
              <p className="mt-3 text-xs text-muted-foreground">{t('tooling_separate_note', lang)}</p>
          </CollapsibleCard>

          {/* ===================== ESTUDOS / DESENVOLVIMENTOS (informativo) ===================== */}
          <CollapsibleCard
            id="studies"
            title={t('studies_section', lang)}
          >
              {!(Array.isArray(quotation?.study_lines) && quotation.study_lines.length) ? (
                <p className="text-xs text-muted-foreground">{t('studies_no_lines', lang)}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-2 py-1 text-left">{t('tooling_line_label', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('tooling_cost', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('tooling_margin', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('tooling_guarantee_shots', lang)}</th>
                        <th className="px-2 py-1 text-right">{t('tooling_sale', lang)}</th>
                        <th className="px-2 py-1 w-10" aria-label="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(quotation?.study_lines) ? quotation.study_lines : []).map((l, i) => {
                        const studyHl = highlight('study_lines');
                        return (
                          <tr key={l._key ?? l.id ?? i} className={`border-t border-border ${studyHl ? 'bg-amber-50/60 dark:bg-amber-950/25' : ''}`}>
                            <td className="px-2 py-1">
                              <Input
                                type="text"
                                disabled={!canEditTechnicalQuotation}
                                className={studyHl ? DIFF_RING : ''}
                                placeholder={t('studies_label_placeholder', lang)}
                                value={l.label || ''}
                                onChange={(e) => updateStudyLine(i, 'label', e.target.value)}
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                type="number"
                                step="1"
                                disabled={!canEditTechnicalQuotation}
                                className={studyHl ? DIFF_RING : ''}
                                value={l.cost_eur ?? 0}
                                onChange={(e) => updateStudyLine(i, 'cost_eur', parseFloat(e.target.value) || 0)}
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                type="number"
                                step="0.1"
                                disabled={!canEditTechnicalQuotation}
                                className={studyHl ? DIFF_RING : ''}
                                value={((l.margin_percent ?? 0) * 100).toFixed(2)}
                                onChange={(e) => updateStudyLine(i, 'margin_percent', (parseFloat(e.target.value) || 0) / 100)}
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                type="number"
                                step="1"
                                min="0"
                                disabled={!canEditTechnicalQuotation}
                                className={studyHl ? DIFF_RING : ''}
                                value={l.guarantee_shots ?? ''}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') {
                                    updateStudyLine(i, 'guarantee_shots', null);
                                  } else {
                                    const parsed = parseInt(raw, 10);
                                    updateStudyLine(i, 'guarantee_shots', Number.isFinite(parsed) ? parsed : null);
                                  }
                                }}
                              />
                            </td>
                            <td className="px-2 py-1 text-right tnum">
                              {formatEuro((l.cost_eur || 0) * (1 + (l.margin_percent || 0)), lang)}
                            </td>
                            <td className="px-2 py-1 text-right">
                              {canEditTechnicalQuotation ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  aria-label={t('studies_remove_line', lang)}
                                  onClick={() => removeStudyLine(i)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {canEditTechnicalQuotation && (
                <div className="mt-3">
                  <Button type="button" size="sm" variant="outline" onClick={addStudyLine}>
                    <Plus className="h-4 w-4" /> {t('studies_add_line', lang)}
                  </Button>
                </div>
              )}
              <p className="mt-3 text-xs text-muted-foreground">{t('studies_separate_note', lang)}</p>
          </CollapsibleCard>

          {/* ===================== ÉCART INVENTAIRE ===================== */}
          <CollapsibleCard
            id="shrink"
            title={t('shrink_section', lang)}
          >
              <PercentField id="inv-shrink"
                label={t('inventory_shrink_label', lang)}
                value={quotation?.inventory_shrink}
                step={0.1}
                disabled={!canEditTechnicalQuotation}
                highlight={highlight('quotation.inventory_shrink')}
                onChange={(v) => updateField('inventory_shrink', v || 0)} />
              <p className="mt-2 text-xs text-muted-foreground">{t('inventory_shrink_help', lang)}</p>
          </CollapsibleCard>
        </div>

        {/* RIGHT column: results + docs + history */}
        <div className="space-y-4">
          <CollapsibleCard
            id="cross_profile_notes"
            title={(
              <>
                <MessageSquare className="h-4 w-4" />
                {t('cross_profile_notes_title', lang)}
              </>
            )}
            contentClassName="space-y-3"
          >
              <p className="text-xs text-muted-foreground">{t('cross_profile_notes_desc', lang)}</p>
              <Textarea
                rows={3}
                value={crossNoteText}
                onChange={(e) => setCrossNoteText(e.target.value)}
                placeholder={t('cross_profile_notes_placeholder', lang)}
              />
              <Button type="button" size="sm" disabled={crossNoteBusy || !crossNoteText.trim()} onClick={handleAddCrossProfileNote}>
                {t('cross_profile_notes_post', lang)}
              </Button>
              {crossProfileNotes.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('cross_profile_notes_empty', lang)}</p>
              ) : (
                <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
                  {crossProfileNotes.map((n) => (
                    <li key={n.id} className="rounded-md bg-muted/50 p-2.5">
                      <div className="text-xs text-muted-foreground">
                        {(userDisplayMap[n.user_id]?.full_name
                          || userDisplayMap[n.user_id]?.username
                          || `#${n.user_id}`)}{' '}
                        ·{' '}
                        {n.created_at ? new Date(n.created_at).toLocaleString(lang === 'en' ? 'en-GB' : 'pt-PT') : ''}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap">{n.text}</div>
                    </li>
                  ))}
                </ul>
              )}
          </CollapsibleCard>

          <CollapsibleCard
            id="results"
            className="border-primary/50 bg-primary/5"
            title={t('results_section', lang)}
          >
              {calcResult ? (
                <div>
                  <CostRow
                    label={t('annual_volume', lang)}
                    value={rfq.annual_volume != null ? `${formatNumber(rfq.annual_volume, lang)} ${t('pieces_unit', lang)}` : '—'}
                  />
                  <Separator className="my-3" />
                  <CostRow label={t('vm_section', lang)} value={formatEuro(calcResult.vm_eur_per_piece, lang)} />
                  <CostRow label={t('va_foundry_section', lang)} value={formatEuro(calcResult.va_foundry_eur_per_piece, lang)} />
                  <CostRow label={t('va_finishing_section', lang)} value={formatEuro(calcResult.va_finishing_eur_per_piece, lang)} />
                  {/* Aparece apenas quando há trabalhos extra activos para
                      manter o painel limpo em peças sem maquinagem/controlo. */}
                  {Number(calcResult.va_extra_works_eur_per_piece || 0) > 0 && (
                    <CostRow
                      label={t('va_machining_control_label', lang)}
                      value={formatEuro(calcResult.va_extra_works_eur_per_piece, lang)}
                    />
                  )}
                  <CostRow label={t('components_section', lang)} value={formatEuro(calcResult.components_eur_per_piece, lang)} />
                  <CostRow label={t('subcontract_section', lang)} value={formatEuro(calcResult.subcontract_eur_per_piece, lang)} />
                  <CostRow label={t('packaging_section', lang)} value={formatEuro(calcResult.packaging_eur_per_piece, lang)} />
                  <CostRow label={t('transport_section', lang)} value={formatEuro(calcResult.transport_eur_per_piece, lang)} />
                  <CostRow label={t('production_cost', lang)} value={formatEuro(calcResult.production_cost_eur, lang)} strong />

                  {canViewMargin && (<>
                  <Separator className="my-3" />
                  <div className="sub-section-label mb-2">
                    {t('commercial_margins_segmented', lang)}
                  </div>
                  <p className="mb-3 text-xs text-muted-foreground">{t('commercial_margins_segmented_help', lang)}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <PercentField
                      id="margin-int-ops"
                      label={t('margin_internal_ops', lang)}
                      value={quotation?.margin_internal_ops_percent}
                      step={0.1}
                      disabled={!canEditCommercialMarginFields}
                      onChange={(v) => updateField('margin_internal_ops_percent', v)}
                    />
                    <PercentField
                      id="margin-pack"
                      label={t('margin_packaging', lang)}
                      value={quotation?.margin_packaging_percent}
                      step={0.1}
                      disabled={!canEditCommercialMarginFields}
                      onChange={(v) => updateField('margin_packaging_percent', v)}
                    />
                    <PercentField
                      id="margin-trans"
                      label={t('margin_transport', lang)}
                      value={quotation?.margin_transport_percent}
                      step={0.1}
                      disabled={!canEditCommercialMarginFields}
                      onChange={(v) => updateField('margin_transport_percent', v)}
                    />
                    <PercentField
                      id="margin-extra-works"
                      label={t('margin_extra_works', lang)}
                      value={quotation?.margin_extra_works_percent}
                      step={0.1}
                      disabled={!canEditCommercialMarginFields}
                      onChange={(v) => updateField('margin_extra_works_percent', v)}
                    />
                    <PercentField
                      id="margin-subcontract"
                      label={t('margin_subcontract', lang)}
                      value={quotation?.margin_subcontract_percent}
                      step={0.1}
                      disabled={!canEditCommercialMarginFields}
                      onChange={(v) => updateField('margin_subcontract_percent', v)}
                    />
                    <PercentField
                      id="margin-components"
                      label={t('margin_components', lang)}
                      value={quotation?.margin_components_percent}
                      step={0.1}
                      disabled={!canEditCommercialMarginFields}
                      onChange={(v) => updateField('margin_components_percent', v)}
                    />
                  </div>

                  <Separator className="my-3" />
                  <div className="sub-section-label mb-2">
                    {t('pricing_buckets', lang)}
                  </div>
                  {(() => {
                    // ``details`` é a única fonte de verdade para as margens
                    // efectivas (lidas directamente da cotação; default 0 quando
                    // vazias). Vem de ``liveBuckets`` (preview imediato em modo
                    // edição) ou, em fallback, do snapshot do servidor.
                    const details = liveBuckets || calcResult?.pricing?.details;
                    const vaInternal =
                      liveBuckets?.bucket_va_internal
                      ?? calcResult?.bucket_va_internal
                      ?? 0;
                    const extraWorks =
                      liveBuckets?.bucket_extra_works
                      ?? calcResult?.bucket_extra_works
                      ?? 0;
                    const packagingBkt =
                      liveBuckets?.bucket_packaging
                      ?? calcResult?.bucket_packaging
                      ?? 0;
                    const transportBkt =
                      liveBuckets?.bucket_transport
                      ?? calcResult?.bucket_transport
                      ?? 0;
                    return (
                      <>
                        <CostRow
                          label={bucketVmLabel(lang, details)}
                          value={formatEuro(liveBuckets?.bucket_vm ?? calcResult.bucket_vm, lang)}
                        />
                        <CostRow
                          label={bucketVaInternalLabel(lang, details)}
                          value={formatEuro(vaInternal, lang)}
                        />
                        {/* Linha de Trabalhos Extra só aparece quando há
                            valor — evita poluir o card em peças sem
                            maquinagem/controlo dimensional. */}
                        {extraWorks > 0 && (
                          <CostRow
                            label={bucketExtraWorksLabel(lang, details)}
                            value={formatEuro(extraWorks, lang)}
                          />
                        )}
                        <CostRow
                          label={bucketPackagingLabel(lang, details)}
                          value={formatEuro(packagingBkt, lang)}
                        />
                        <CostRow
                          label={bucketTransportLabel(lang, details)}
                          value={formatEuro(transportBkt, lang)}
                        />
                        <CostRow
                          label={bucketComponentsLabel(lang, details)}
                          value={formatEuro(liveBuckets?.bucket_components ?? calcResult.bucket_components, lang)}
                        />
                        <CostRow
                          label={bucketSubcontractLabel(lang, details)}
                          value={formatEuro(liveBuckets?.bucket_subcontract ?? calcResult.bucket_subcontract, lang)}
                        />
                      </>
                    );
                  })()}

                  <div className="mt-3 flex items-center justify-between border-t-2 border-primary pt-3 text-lg font-bold">
                    <span>{t('sale_price', lang)}</span>
                    <span className="tnum text-primary">{formatEuro(liveBuckets?.sale_price ?? calcResult.sale_price_eur, lang)}</span>
                  </div>
                  <CostRow
                    label={t('global_margin', lang)}
                    value={`${(((liveBuckets?.margin_global_percent ?? calcResult.margin_global_percent) ?? 0) * 100).toFixed(2)} %`}
                    strong
                  />
                  <CostRow label={t('margin_eur', lang)} value={formatEuro(liveBuckets?.margin_eur ?? calcResult.margin_eur, lang)} />

                  {calcResult.tooling_total_sale_eur > 0 && (
                    <>
                      <Separator className="my-3" />
                      <CostRow
                        label={t('tooling_total_separate', lang)}
                        value={formatEuro(calcResult.tooling_total_sale_eur, lang)}
                        strong
                      />
                      <p className="text-xs text-muted-foreground">{t('tooling_separate_note', lang)}</p>
                    </>
                  )}
                  </>)}

                  {Array.isArray(calcResult.warnings) && calcResult.warnings.length > 0 && (
                    <div className="mt-4 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-100">
                      <div className="mb-1 font-semibold">{t('warnings', lang)}</div>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {calcResult.warnings.map((w, i) => <li key={i}>{t(`warn_${w.code}`, lang) || w.message || w.code}</li>)}
                      </ul>
                    </div>
                  )}

                  {calcResult.kpis && (
                    <>
                      <Separator className="my-4" />
                      <div className="sub-section-label mb-2">KPIs</div>
                      {calcResult.kpis.vm_share_percent != null && (
                        <CostRow label={t('vm_share', lang)} value={`${calcResult.kpis.vm_share_percent}%`} />
                      )}
                      {/* VA anual esperado — preferimos o valor live (reage
                          a edições não gravadas das margens segmentadas);
                          fallback para o snapshot do backend. Mostra "—"
                          quando não há volume anual definido. */}
                      {(() => {
                        const vol = Number(rfq?.annual_volume || 0);
                        const bucketVa =
                          liveBuckets?.bucket_va_internal
                          ?? calcResult?.bucket_va_internal
                          ?? null;
                        const liveVa = liveBuckets?.expected_annual_va_eur;
                        const snapVa = calcResult.kpis.expected_annual_va_eur;
                        const value = liveVa != null && liveVa > 0
                          ? liveVa
                          : (snapVa != null ? snapVa : null);
                        if (value == null || value === 0 || vol <= 0 || bucketVa == null) return null;
                        const detail = t('expected_annual_va_detail', lang)
                          .replace('{volume}', formatNumber(vol, lang))
                          .replace('{rate}', formatNumber(bucketVa, lang, 4));
                        return (
                          <>
                            <CostRow
                              label={t('expected_annual_va', lang)}
                              value={formatEuro(value, lang)}
                            />
                            <p className="-mt-1 mb-1 border-b border-dashed border-border pb-1.5 text-[11px] leading-snug text-muted-foreground">
                              {detail}
                            </p>
                          </>
                        );
                      })()}
                      {calcResult.kpis.annual_revenue_eur != null && (
                        <CostRow label={t('annual_revenue', lang)} value={formatEuro(calcResult.kpis.annual_revenue_eur, lang)} />
                      )}
                      {calcResult.kpis.total_scrap_estimate_percent != null && (
                        <CostRow label={t('total_scrap_estimate', lang)} value={`${calcResult.kpis.total_scrap_estimate_percent}%`} />
                      )}
                    </>
                  )}
                  <div className="mt-3 text-xs text-muted-foreground">
                    {t('engine_mode', lang)}: {calcResult.mode} · {t('engine_version', lang)} v{calcResult.engine_version}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('save_quotation_to_see', lang)}</p>
              )}
          </CollapsibleCard>

          {/* PnL cross-check audit — método paralelo de validação contra a
              estrutura da fábrica (FactoryPnLConfig). Snapshot vem do backend
              dentro de QuotationResponse.pnl_audit_snapshot. FE-04: só visível
              a quem tem rfq_view_margin (o backend já redige o snapshot). */}
          {canViewMargin && (
            <PnLAuditPanel snapshot={quotation?.pnl_audit_snapshot} lang={lang} rfqId={rfq?.id} />
          )}

          <CollapsibleCard
            id="documents"
            title={(
              <>
                <FileText className="h-4 w-4" aria-hidden="true" />
                {t('documents', lang)} ({documents.length})
              </>
            )}
            contentClassName="space-y-3"
          >
              {/* Os 3 slots G1 já aparecem no painel G1 (só-comercial em in_preparation);
                  para todos os outros estados/utilizadores mostramos aqui o resumo. */}
              {!(rfq.status === 'in_preparation' && isCommercialUser) && (
                <EssentialDocumentsSection
                  rfqId={id}
                  documents={documents}
                  canEdit={canAttachDocuments}
                  onChanged={reloadDocuments}
                  lang={lang}
                  alloys={alloys}
                  rfq={rfq}
                  quotation={quotation}
                  onApplySuggestions={applySuggestions}
                  onMarkSuggested={markSuggestedFields}
                />
              )}
              {canAttachDocuments && (
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-accent">
                  <Upload className="h-4 w-4" />
                  {t('attach_document', lang)}
                  <input type="file" className="sr-only" onChange={handleFileUpload} />
                </label>
              )}
              {(() => {
                // A lista existente fica para anexos genéricos (sem slot G1) — evita
                // duplicação visual com a sub-secção de slots G1.
                const otherDocs = documents.filter((d) => !d.slot);
                if (otherDocs.length === 0) {
                  return <p className="text-sm text-muted-foreground">{t('no_documents', lang)}</p>;
                }
                return (
                  <ul className="divide-y divide-border">
                    {otherDocs.map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                        <span className="min-w-0 flex-1 truncate">{d.filename}</span>
                        <span className="shrink-0 text-xs text-muted-foreground tnum">
                          {(((d.file_size ?? d.size) || 0) / 1024).toFixed(0)} KB
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0 gap-1"
                          onClick={() => handleDownloadDocument(d)}
                          aria-label={`${t('download_document', lang)}: ${d.filename}`}
                        >
                          <Download className="h-3.5 w-3.5" />
                          {t('download', lang)}
                        </Button>
                      </li>
                    ))}
                  </ul>
                );
              })()}
          </CollapsibleCard>

          <CollapsibleCard
            id="status_history"
            title={(
              <>
                {t('status_history', lang)}
                {history.length > 0 && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
                    {history.length}
                  </span>
                )}
              </>
            )}
          >
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('no_history', lang)}</p>
              ) : (
                <ul className="divide-y divide-border">
                  {history.map((h) => {
                    const risk = extractRiskFromJustification(h.justification);
                    const reworkN = extractReworkVersion(h.justification);
                    const formVn = extractFormVersion(h.justification);
                    const matchedVersion = formVn ? versions.find((v) => v.version_number === formVn) : null;
                    const snapVersion = h.quotation_snapshot?.calc_version;
                    return (
                    <li key={h.id} className="space-y-1 py-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={statusBadgeVariant(h.new_state)}>
                          {t(h.new_state, lang)}
                        </Badge>
                        {reworkN && (
                          <Badge variant="info" className="gap-1">
                            <Undo2 className="h-3 w-3" />
                            {t('rework_version_badge', lang)}{reworkN}
                          </Badge>
                        )}
                        {formVn && (
                          <button
                            type="button"
                            onClick={() => matchedVersion && handleOpenVersion(matchedVersion.id)}
                            className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
                            disabled={!matchedVersion}
                            title={matchedVersion ? t('view_version', lang) : undefined}
                          >
                            <HistoryIcon className="h-3 w-3" />
                            {t('version_number_short', lang).replace('{n}', String(formVn))}
                            {matchedVersion && matchedVersion.changes_count > 0 && (
                              <span className="text-muted-foreground">· {matchedVersion.changes_count}</span>
                            )}
                          </button>
                        )}
                        {snapVersion && !reworkN && (
                          <span className="text-xs text-muted-foreground tnum">v{snapVersion}</span>
                        )}
                        {risk && (
                          <Badge
                            variant={risk === 'high' ? 'destructive' : risk === 'medium' ? 'warning' : 'success'}
                          >
                            {t(`risk_${risk}`, lang)}
                          </Badge>
                        )}
                        <span className="ml-auto text-xs text-muted-foreground tnum">
                          {new Date(h.created_at).toLocaleString(
                            lang === 'en' ? 'en-GB' : lang === 'fr' ? 'fr-FR' : 'pt-PT'
                          )}
                        </span>
                      </div>
                      <div className="text-muted-foreground">{h.user}</div>
                      {h.justification && (
                        <div className="flex items-start gap-1.5 rounded-md bg-warning/10 p-2 text-xs text-foreground">
                          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                          <span>{h.justification}</span>
                        </div>
                      )}
                      {h.quotation_snapshot && Object.keys(h.quotation_snapshot).length > 0 && (
                        <div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => setExpandedSnapshot(expandedSnapshot === h.id ? null : h.id)}
                          >
                            {expandedSnapshot === h.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            {expandedSnapshot === h.id ? t('hide_quotation', lang) : t('view_quotation_snapshot', lang)}
                          </Button>
                          {expandedSnapshot === h.id && (
                            <div className="mt-2 rounded-md bg-muted p-2 text-xs">
                              {[
                                [t('snapshot_alloy', lang), h.quotation_snapshot.alloy_code],
                                [t('snapshot_piece_weight', lang), h.quotation_snapshot.piece_weight_kg],
                                [t('snapshot_runner_weight', lang), h.quotation_snapshot.runner_weight_kg],
                                [t('snapshot_projected_area', lang), h.quotation_snapshot.projected_area_cm2],
                                [t('snapshot_cadence', lang), h.quotation_snapshot.shots_per_hour],
                                [
                                  'TRS',
                                  h.quotation_snapshot.trs_percent != null
                                    ? `${(h.quotation_snapshot.trs_percent * 100).toFixed(0)}%`
                                    : null,
                                ],
                                [t('snapshot_cavities', lang), h.quotation_snapshot.num_cavities],
                                [
                                  t('snapshot_margin', lang),
                                  h.quotation_snapshot.margin_percent != null
                                    ? `${(h.quotation_snapshot.margin_percent * 100).toFixed(1)}%`
                                    : null,
                                ],
                                [
                                  t('snapshot_sale_price', lang),
                                  h.quotation_snapshot.calc_sale_price_eur != null
                                    ? Number(h.quotation_snapshot.calc_sale_price_eur).toFixed(4)
                                    : null,
                                ],
                                [
                                  t('snapshot_production_cost', lang),
                                  h.quotation_snapshot.calc_production_cost_eur != null
                                    ? Number(h.quotation_snapshot.calc_production_cost_eur).toFixed(4)
                                    : null,
                                ],
                              ]
                                .filter(([, v]) => v != null && v !== '')
                                .map(([label, value]) => (
                                  <div
                                    key={label}
                                    className="flex items-center justify-between border-b border-border/50 py-1 last:border-0"
                                  >
                                    <span className="text-muted-foreground">{label}</span>
                                    <span className="font-medium tnum">{value}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                    );
                  })}
                </ul>
              )}
          </CollapsibleCard>
        </div>
      </div>
    </div>
    </CardFoldProvider>
  );
}
